import { MAX_PICTURE_BYTES, pictureTooBig } from './uploadLimits'
import type { FloorOverview } from './floorOverview'
import type { FloorSeries } from './liveDash'
import type { Estimate, EstimateAccuracy, QueueEstimate, TicketEstimate } from './estimate'
import type {
  AccessToken,
  NotLive,
  BookingBy,
  BookingReport,
  FeedAnswer,
  CrossCell,
  FetchedDim,
  ReportRow,
  Appointment,
  ApprovalSettings,
  AuditEvent,
  BookingBranchSettings,
  BookingBranchView,
  BookingForm,
  BookingFormFields,
  BookingChannel,
  BookingKey,
  BookingPolicy,
  BookingServiceRow,
  BookingSlot,
  BookingView,
  Branch,
  CatalogueEntry,
  CheckInRules,
  BranchModule,
  ClosedDay,
  Connector,
  ConnectorCall,
  DeliveredOutcome,
  DeliveredService,
  Delivery,
  DirectorySettings,
  DirectoryTestResult,
  SAMLDraft,
  SAMLFound,
  SAMLServiceProvider,
  SAMLSettings,
  KerberosDraft,
  KerberosKey,
  KerberosSettings,
  SSOChoice,
  SSODraft,
  SSOEndpoints,
  SSOProtocol,
  SSOSettings,
  FinMode,
  GroupMapping,
  Identity,
  ImportAnswer,
  LicenceStatus,
  MailServerDraft,
  MailServerSettings,
  Mark,
  NetworkSummary,
  NotificationSettings,
  NotificationsView,
  Organisation,
  OrganisationChoice,
  OrgRole,
  Permission,
  Queue,
  QueueAlertSetting,
  ReportSchedule,
  ReportSummary,
  ReportView,
  ViewDraft,
  Outcome,
  RequestKind,
  BreakReason,
  RequestView,
  Screen,
  Service,
  ScheduleDraft,
  ScheduleList,
  ScreenApp,
  ScreenAppAnswer,
  ScreenView,
  SettingView,
  SMSGateway,
  Unit,
  Snapshot,
  StaffMember,
  KnownCustomer,
  NoteEntry,
  Ticket,
  TicketHistoryEvent,
  TicketReportPage,
  TicketReportSummary,
  VisitField,
  Webhook,
  WebhookTestResult,
  WorkProfile,
} from './types'
import type {
  BranchDoc,
  BranchSetup,
  BranchSetupAnswer,
  CounterSessionView,
  EquipmentDoc,
  EquipmentDocumentAnswer,
  EquipmentProfileRow,
  ImportRequest,
  OperationDoc,
  OperationDocumentAnswer,
  OperationProfileRow,
  ProfileImportPreview,
  ProfileProblem,
  SaveBranchSetupAnswer,
  SetupStatusLine,
  SetupVersion,
  WordingOverride,
  WordingView,
} from './types'
import type { StaffLanguage } from './staffText'
import type { ClientErrorReport } from './clientErrors'
import type { LayoutDocument, LayoutKind, LayoutText, MediaFit, MediaItem, MediaKind } from '../layout/types'
import { packCalled, unpackCalled } from '../layout/calledScreen'
import type { BranchGroup } from './branchNetwork'
import type { FeedbackVisit } from './feedback'
import type { CounterVisit } from './counterDisplay'
import type { FollowView } from './follow'
import type { DesignBranch, DesignResult } from './designBranches'
import type { SlipsByService } from './slips'
import type { BreakKind } from './orgOptions'
import type { BranchSettingsPatch, BranchSettingsView, SettingKey } from './branchSettings'
import type { LanguageInfo, LanguageSettings } from './i18n/languages'

/** GET/PUT /organisation/languages: the settings, and every language the product knows. */
export interface OrgLanguagesView extends LanguageSettings {
  organisation: string
  known: LanguageInfo[]
}
import type { PivotFields, PivotResult, PivotSpec } from '../reports/pivot/types'
import { contextOrganisation, scopedPath } from './ownerOrg'
import { appendBranches } from '../reports/ticketReport'

const pivotQuery = (branches: string[], spec: PivotSpec) => {
  const p = new URLSearchParams()
  appendBranches(p, branches)
  p.set('spec', JSON.stringify(spec))
  return p.toString()
}
const orgQuery = (organisation?: string) => (organisation ? `?organisation=${encodeURIComponent(organisation)}` : '')

// The client for the HTTP API.
//
// Two things it always does. It sends an Idempotency-Key with every request that
// changes something, so a reply lost to a flaky link cannot become a second ticket
// or a second call. And it turns an RFC 9457 problem document into an error whose
// message is the sentence the server wrote — an operator should read what went
// wrong, not "Request failed with status code 409".
// The code, a reason and the arguments travel with it too, so a screen can say the
// same refusal in the reader's language (lib/staffErrors).

const BASE = '/api/v1'

/** A design somebody can start from. The document travels with it so the gallery
 *  can draw it with the renderer the wall uses. */
export interface LayoutTemplate {
  name: string
  label: string
  description: string
  document: LayoutDocument
}

/**
 * A design in the organisation's library. It belongs to no place; units (in their
 * settings) and, for a slip, services choose it, and one per kind may be the
 * default for everything that chose nothing.
 */
export interface StoredLayout {
  id: string
  tenantId: string
  kind: LayoutKind
  name?: string
  /** Used by every branch and machine that chose no design of this kind. */
  isDefault?: boolean
  /** Printed tickets only. */
  serviceIds?: string[]
  /** Kiosk designs only: the operation profile it belongs to (equipment spec §6). */
  operationProfileId?: string
  draft: LayoutDocument
  published?: LayoutDocument
  version: number
  publishedAt?: string
  publishedBy?: string
  updatedAt: string
  /** Counts the saves of the draft; see api.saveDraft's base. Absent from a server
   *  older than 0063. */
  draftRevision?: number
}

/** A stored design with its documents in the browser's shape (layout/calledScreen.ts). */
function unpackStored(l: StoredLayout): StoredLayout {
  return { ...l, draft: unpackCalled(l.draft), published: unpackCalled(l.published) }
}

export interface LayoutVersion {
  version: number
  document: LayoutDocument
  publishedAt: string
  publishedBy?: string
  note?: string
}

export interface LayoutAsset {
  id: string
  filename: string
  contentType: string
  width: number
  height: number
  sizeBytes: number
  createdAt: string
  /** The signage library holds it; it is deleted on the Signage page. */
  library?: boolean
}

/** A design film and who names it (store.DesignFilm). */
export interface DesignFilm {
  id: string
  filename: string
  sizeBytes: number
  createdAt: string
  live: boolean
  versions: number
  /** Uploaded within the last day; deletable then only by its uploader (yours). */
  new: boolean
  yours: boolean
}

/** A file in the organisation's shared signage library (internal/signage.MediaItem). */
export interface SignageMedia {
  id: string
  assetId: string
  posterAssetId?: string
  name: string
  kind: MediaKind
  bytes: number
  durationMs?: number
  /** A picture's pixels; absent for a film. */
  width?: number
  height?: number
  createdAt: string
  createdBy?: string
  usedBy: string[]
}

export interface PlaylistEntry {
  mediaItemId: string
  seconds: number
  fit: MediaFit
}

export interface TickerLine {
  az: string
  ru: string
  en: string
}

export interface Playlist {
  id: string
  name: string
  version: number
  entries: PlaylistEntry[]
  ticker: TickerLine[]
  updatedAt: string
  updatedBy?: string
}

export interface PlaylistSummary extends Playlist {
  items: number
  totalSeconds: number
  designs: string[]
  campaigns: string[]
  /** The first item, for the card; absent for an empty playlist. */
  cover?: { mediaItemId: string; assetId: string; kind: MediaKind; posterAssetId?: string }
}

export type SignagePlace = 'board' | 'kioskIdle' | 'kioskBanner'

export interface Campaign {
  id: string
  name: string
  playlistId: string
  startDate: string
  endDate: string
  weekdays: number[]
  fromMin: number
  toMin: number
  priority: 'normal' | 'high'
  active: boolean
  branchIds: string[]
  places: SignagePlace[]
  /** What the list says of it now, by the rule the TVs play by (internal/signage StateOf). */
  state?: 'off' | 'scheduled' | 'running' | 'ended' | 'outside' | 'outranked' | 'noSeat' | 'empty'
  updatedAt?: string
}

export type CampaignBody = Omit<Campaign, 'id' | 'state' | 'updatedAt'>

export interface BranchSeat {
  id: string
  name: string
  seat: boolean
  timezone: string
  /** A hall screen there shows a published design with a playlist area. */
  playlistArea: boolean
}

export interface SignageNow {
  items: MediaItem[]
  campaigns: Array<{ id: string; name: string }>
  design?: string
  changesAt?: string
}

/** A hall TV as the Signage page's Screens tab lists it (GET /signage/screens). */
export interface SignageScreen {
  id: string
  branchId: string
  branchName: string
  branchCode: string
  code: string
  name: string
  online: boolean
  /** Last seen since the server started; absent when not seen since. */
  seenAt?: string
  design?: { id: string; name: string }
  playlistArea: boolean
  seat: boolean
  items: MediaItem[]
  campaigns: Array<{ id: string; name: string }>
  changesAt?: string
  skipped?: { assetId: string; name?: string; kind: string; at: string; count: number }
}

/** A file a screen should hold, and from when it plays (the layout answer's prefetch). */
export interface PrefetchFile {
  id: string
  bytes: number
  from: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly correlationId?: string
  readonly args?: Record<string, string>
  readonly reason?: string
  /** A profile or branch-setup document's faults (422 PROFILE_INVALID, DRAFT_INVALID, FILE_INVALID). */
  readonly problems?: ProfileProblem[]

  constructor(
    status: number,
    code: string,
    message: string,
    correlationId?: string,
    args?: Record<string, string>,
    reason?: string,
    problems?: ProfileProblem[],
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.correlationId = correlationId
    this.args = args
    this.reason = reason
    this.problems = problems
  }

  /** Whether the operator simply needs to sign in again. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Nobody is waiting is not a failure, and screens say so in plain words. */
  get isEmptyQueue(): boolean {
    return this.code === 'QUEUE_EMPTY'
  }

  /** The installation hosts several organisations and has not been told which. */
  get needsOrganisation(): boolean {
    return this.code === 'ORGANISATION_REQUIRED'
  }

  /** Signed out for standing still, rather than for being unknown. */
  get wasIdle(): boolean {
    return this.code === 'SESSION_IDLE'
  }

}

/** What the Connectors form sends: everything a connector holds but its secret. */
export interface ConnectorBody {
  name: string
  url: string
  methods: string[]
  /** The paths below the address an app may call: exact, or a prefix ending in
   *  /*. Empty allows the address itself and nothing below it. */
  paths: string[]
  headerName: string
  timeoutMs: number
  active: boolean
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Set for anything that changes state. */
  idempotent?: boolean
  signal?: AbortSignal
  /** Gives up after this long; an addressed screen's request defaults to SCREEN_TIMEOUT_MS. */
  timeoutMs?: number
  /** Sent as given: the follow token travels here, never in a path. */
  headers?: Record<string, string>
  /** Outlives the page: a save sent as the tab closes. */
  keepalive?: boolean
}

/** Well inside the kiosk's 25 s idle reset, so a lost answer fails before the screen resets. */
export const SCREEN_TIMEOUT_MS = 15_000

/** How long a design's draft save may take before it counts as not saved. */
export const DRAFT_SAVE_TIMEOUT_MS = 20_000

/** The pauses before each automatic retry of a write that carries a key. */
export const RETRY_DELAYS_MS: readonly number[] = [400, 1500]

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}

  // Every write says JSON, body or not: the server refuses a write without it (CSRF).
  if (options.body !== undefined || (method !== 'GET' && method !== 'HEAD')) headers['Content-Type'] = 'application/json'
  // One key for every attempt below, so the server answers a retry with the first result.
  const key = options.idempotent ? newIdempotencyKey() : undefined
  if (key) headers['Idempotency-Key'] = key
  Object.assign(headers, options.headers ?? {})
  const signal = options.signal ?? deadline(path, options.timeoutMs)

  const init: RequestInit = {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    // Same-origin: the session cookie is HttpOnly and belongs to this origin only.
    credentials: 'same-origin',
    signal,
    keepalive: options.keepalive,
  }
  let response: Response
  for (let attempt = 0; ; attempt++) {
    const retryIn = key ? RETRY_DELAYS_MS[attempt] : undefined
    try {
      response = await fetch(BASE + scopedPath(path), init)
    } catch (err) {
      if (retryIn === undefined || signal?.aborted) throw err
      await pause(retryIn, signal)
      continue
    }
    if (retryIn === undefined || !(await worthRetrying(response))) break
    await pause(retryIn, signal)
  }

  // 304 answers only a caller that named what it holds (If-None-Match).
  if (response.status === 204 || response.status === 304) return undefined as T

  const text = await response.text()
  const payload = text ? safeParse(text) : undefined

  if (!response.ok) throw refusal(path, response, payload)
  return payload as T
}

/** The time limit a request runs under, if any. */
function deadline(path: string, timeoutMs?: number): AbortSignal | undefined {
  const ms = timeoutMs ?? (path.startsWith('/screen/') ? SCREEN_TIMEOUT_MS : undefined)
  if (ms === undefined || typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return undefined
  return AbortSignal.timeout(ms)
}

/** A gateway fault, or the first attempt still running: the same key may ask again. */
async function worthRetrying(response: Response): Promise<boolean> {
  if (response.status === 502 || response.status === 503 || response.status === 504) return true
  if (response.status !== 409) return false
  const text = await response.clone().text().catch(() => '')
  return text.includes('IDEMPOTENT_REQUEST_IN_PROGRESS')
}

function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason)
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(timer)
      reject(signal.reason)
    }, { once: true })
  })
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/** POSTs a signed file byte for byte: re-encoding it would break its signature. */
async function postFile<T>(path: string, file: File): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: await file.arrayBuffer(),
    credentials: 'same-origin',
  })
  const text = await response.text()
  const payload = text ? safeParse(text) : undefined
  if (!response.ok) throw refusal(path, response, payload)
  return payload as T
}

/** The ApiError a failed response becomes, shared by request() and sendForm() so both announce a 401. */
function refusal(path: string, response: Pick<Response, 'status'>, payload: unknown): ApiError {
  const problem = payload as
    | {
        code?: string
        detail?: string
        title?: string
        correlationId?: string
        args?: Record<string, string>
        reason?: string
        problems?: ProfileProblem[]
      }
    | undefined
  const error = new ApiError(
    response.status,
    problem?.code ?? 'UNKNOWN',
    problem?.detail ?? problem?.title ?? `The server answered ${response.status}.`,
    problem?.correlationId,
    problem?.args,
    problem?.reason,
    problem?.problems,
  )
  // Excluded: paths with no session to lose — sign-in, "am I signed in",
  // an addressed screen, and a customer's follow link.
  if (
    error.status === 401 &&
    !path.startsWith('/auth/login') &&
    !path.startsWith('/auth/me') &&
    !path.startsWith('/screen/') &&
    !path.startsWith('/follow') &&
    !path.startsWith('/client-errors')
  ) {
    window.dispatchEvent(new CustomEvent('qms:signed-out', { detail: { code: error.code, message: error.message } }))
  }
  return error
}

/** A multipart POST, refused — including the 401 → sign-out announcement — the way request() refuses. */
async function sendForm<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(BASE + scopedPath(path), { method: 'POST', body, credentials: 'same-origin' })
  const text = await response.text()
  const payload = text ? safeParse(text) : undefined
  if (!response.ok) throw refusal(path, response, payload)
  return payload as T
}

/** sendForm over XMLHttpRequest, which alone reports how much of the body was sent (0 to 1). */
function sendFormWithProgress<T>(path: string, body: FormData, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const aborted = () => new DOMException('', 'AbortError')
    if (signal?.aborted) {
      reject(aborted())
      return
    }
    const xhr = new XMLHttpRequest()
    const stop = () => xhr.abort()
    signal?.addEventListener('abort', stop, { once: true })
    const done = () => signal?.removeEventListener('abort', stop)
    xhr.open('POST', BASE + scopedPath(path))
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => {
      done()
      const payload = xhr.responseText ? safeParse(xhr.responseText) : undefined
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload as T)
      else reject(refusal(path, { status: xhr.status }, payload))
    }
    xhr.onerror = xhr.ontimeout = () => {
      done()
      reject(new TypeError())
    }
    xhr.onabort = () => {
      done()
      reject(aborted())
    }
    xhr.send(body)
  })
}

/**
 * A random id in UUID form: an Idempotency-Key, or a client's id for what it adds.
 *
 * crypto.randomUUID is not available on an insecure origin, which a branch running
 * plain HTTP on an internal address would be. The fallback is a random id of the
 * same shape rather than a failure to print a ticket.
 */
export function newIdempotencyKey(): string {
  const source: Crypto | undefined = typeof crypto === 'undefined' ? undefined : crypto

  if (source && typeof source.randomUUID === 'function') return source.randomUUID()

  const bytes = new Uint8Array(16)
  if (source && typeof source.getRandomValues === 'function') source.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** A master account's password was right; the code mailed to it opens the session. */
export interface SignInChallenge {
  challenge: string
  method: 'email'
  /** Where the code went; absent while an owner with no address gives one. */
  sentTo?: string
  /** An on-premises owner with no address: one is asked for, then a code sent there. */
  needsEmail?: boolean
  expiresIn: number
}

export const api = {
  /** An identity, or — for a master account — the challenge its emailed code answers. */
  /** remember false: a session cookie that ends when the browser closes. */
  login: (username: string, password: string, organisation?: string, remember = true) =>
    request<Identity | SignInChallenge>('/auth/login', {
      method: 'POST',
      body: {
        username,
        password,
        ...(organisation ? { organisation } : {}),
        ...(remember ? {} : { remember: false }),
      },
    }),
  loginCode: (challenge: string, code: string, remember = true) =>
    request<Identity>('/auth/login/code', {
      method: 'POST',
      body: { challenge, code, ...(remember ? {} : { remember: false }) },
    }),
  loginEmail: (challenge: string, email: string) =>
    request<SignInChallenge>('/auth/login/email', { method: 'POST', body: { challenge, email } }),

  /** Answers the provider's sign-out address for a session single sign-on opened. */
  logout: () => request<{ signOutUrl?: string } | undefined>('/auth/logout', { method: 'POST' }),

  me: () => request<Identity>('/auth/me'),

  /**
   * The organisations this installation will accept a sign-in for.
   *
   * Empty on the hosted service, where the hostname already says which organisation
   * is meant and a list of every customer is not something to hand out at the door.
   */
  organisations: () => request<{ organisations: OrganisationChoice[] }>('/auth/organisations'),

  /** The sign-in services the sign-in page offers for this organisation (its host, or the one named). */
  ssoChoices: (organisation?: string) =>
    request<{ providers: SSOChoice[] }>(organisation ? `/auth/sso?organisation=${encodeURIComponent(organisation)}` : '/auth/sso'),

  /** The organisation's single sign-on providers; no secret or private key comes back. */
  sso: (organisation?: string) =>
    request<{
      sso: SSOSettings | null
      saml: SAMLSettings | null
      samlSp: SAMLServiceProvider
      protocol: SSOProtocol | ''
      redirectUri: string
      centralOnly: boolean
    }>(organisation ? `/sso?organisation=${encodeURIComponent(organisation)}` : '/sso'),

  /** The installation owner's alone. certificates absent keeps the stored ones; metadata wins over typed fields. */
  saveSAML: (body: SAMLDraft & { organisation?: string; metadataXml?: string; certificates?: string }) =>
    request<{ saml: SAMLSettings; samlSp: SAMLServiceProvider }>('/sso/saml', { method: 'PUT', body }),

  /** Reads a provider's metadata now: the address or document given, else the saved address. */
  testSAML: (organisation: string | undefined, source?: { metadataUrl?: string; metadataXml?: string }) =>
    request<{ found: SAMLFound }>('/sso/saml/test', { method: 'POST', body: { organisation, ...source } }),

  /** The installation owner's alone. clientSecret absent keeps the stored one. */
  saveSSO: (body: SSODraft & { organisation?: string; clientSecret?: string }) =>
    request<{ sso: SSOSettings }>('/sso', { method: 'PUT', body }),

  /** Fetches the provider's discovery document now; issuer empty tries the saved one. */
  testSSO: (organisation: string | undefined, issuer?: string) =>
    request<{ endpoints: SSOEndpoints }>('/sso/test', { method: 'POST', body: { organisation, issuer } }),

  /** Where the sign-in page tries Windows sign-in; '' when the organisation has it off. */
  negotiateChoice: (organisation?: string) =>
    request<{ start: string; auto?: boolean }>(organisation ? `/auth/negotiate?organisation=${encodeURIComponent(organisation)}` : '/auth/negotiate'),

  /** The organisation's Windows sign-in; the keytab never comes back. */
  kerberos: (organisation?: string) =>
    request<{ kerberos: KerberosSettings | null; centralOnly: boolean }>(
      organisation ? `/kerberos?organisation=${encodeURIComponent(organisation)}` : '/kerberos',
    ),

  /** The installation owner's alone. keytab (base64) absent keeps the stored one. */
  saveKerberos: (body: KerberosDraft & { organisation?: string; keytab?: string }) =>
    request<{ kerberos: KerberosSettings }>('/kerberos', { method: 'PUT', body }),

  /** Opens the stored keytab and checks it has the service principal's key. */
  testKerberos: (organisation?: string) =>
    request<{ servicePrincipal: string; kvno: number; keys: KerberosKey[] }>('/kerberos/test', {
      method: 'POST',
      body: { organisation },
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),

  /** The language this person's own screens read in; '' follows their organisation. */
  setMyLanguage: (language: StaffLanguage | '') =>
    request<Identity>('/auth/me', { method: 'PATCH', body: { language } }),

  /** The organisation's default, for everybody who has not chosen. The owner names the organisation. */
  setOrganisationLanguage: (language: StaffLanguage, organisation?: string) =>
    request<{ staffLanguage: StaffLanguage }>('/staff-language', {
      method: 'PUT',
      body: { language, ...(organisation ? { organisation } : {}) },
    }),

  /** The people in one organisation, plus the installation's own accounts.
   *
   *  `organisation` is for the installation's owner, who belongs to none and so has
   *  no list of their own to show. Everybody else sees theirs and the parameter is
   *  ignored. */
  staffList: (organisation?: string) =>
    request<{ staff: StaffMember[] }>(
      organisation ? `/staff?organisation=${encodeURIComponent(organisation)}` : '/staff',
    ),

  createStaff: (body: {
    username: string
    displayName: string
    /** One of the organisation's roles; absent only for another installation
     *  account (owner only — see `installation`). */
    roleId?: string
    added?: Permission[]
    removed?: Permission[]
    branchIds?: string[]
    organisation?: string
    language?: StaffLanguage | ''
    /** Asks for another installation account. Owner only. */
    installation?: boolean
  }) =>
    request<{ staff: StaffMember; firstPassword: string }>('/staff', { method: 'POST', body }),

  updateStaff: (
    staffId: string,
    body: {
      displayName?: string
      roleId?: string
      added?: Permission[]
      removed?: Permission[]
      branchIds?: string[]
      active?: boolean
      /** Hands a local account to the organisation's directory. The way back is
       *  a password reset, not this field: the server refuses "local" here. */
      signIn?: 'directory' | 'sso'
      language?: StaffLanguage | ''
    },
  ) => request<{ staff: StaffMember }>(`/staff/${staffId}`, { method: 'PATCH', body }),

  /** An organisation's roles, each with its permissions, whether it is locked
   *  and how many people hold it. `organisation` is for the installation's
   *  owner, as on staffList; everybody else sees their own and it is ignored. */
  roles: (organisation?: string) =>
    request<{ roles: OrgRole[] }>(organisation ? `/roles?organisation=${encodeURIComponent(organisation)}` : '/roles'),

  /** Answers with the bare role, not wrapped. */
  createRole: (body: { name: string; permissions: Permission[] }, organisation?: string) =>
    request<OrgRole>('/roles', { method: 'POST', body: { ...body, ...(organisation ? { organisation } : {}) } }),

  /** Permissions left out leaves what the role gives alone, a rename only.
   *  Refused for the locked role. Answers with the bare role. */
  updateRole: (roleId: string, body: { name?: string; permissions?: Permission[] }, organisation?: string) =>
    request<OrgRole>(`/roles/${roleId}`, { method: 'PUT', body: { ...body, ...(organisation ? { organisation } : {}) } }),

  /** Refused (409 ROLE_IN_USE, with the count) while anyone holds the role,
   *  (409 ROLE_MAPPED, with the count) while a directory group gives it, and
   *  for the locked role. */
  deleteRole: (roleId: string, organisation?: string) =>
    request<void>(`/roles/${roleId}${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`, {
      method: 'DELETE',
    }),

  /** The role editor's catalogue: the screens it ticks, kept to what the
   *  licence has bought, plus which permissions this caller may hand out. */
  permissionScreens: (organisation?: string) =>
    request<{ screens: Screen[]; mayGive: Permission[] }>(
      organisation ? `/permissions?organisation=${encodeURIComponent(organisation)}` : '/permissions',
    ),

  /** Sets the password an administrator typed for somebody else. */
  setStaffPassword: (staffId: string, password: string) =>
    request<void>(`/staff/${staffId}/password`, { method: 'POST', body: { password } }),

  unlockStaff: (staffId: string) =>
    request<{ staff: StaffMember }>(`/staff/${staffId}/unlock`, { method: 'POST', body: {} }),

  /** Your own access tokens, newest first (revoked or expired ones for 30 days). */
  accessTokens: () => request<{ tokens: AccessToken[] }>('/auth/tokens'),
  /** Makes a token; `secret` is shown once. `expiresInDays` null = no end. */
  createAccessToken: (name: string, expiresInDays: 7 | 30 | 90 | 365 | null) =>
    request<{ token: AccessToken; secret: string }>('/auth/tokens', {
      method: 'POST',
      body: { name, expiresInDays },
    }),
  revokeAccessToken: (id: string) => request<void>(`/auth/tokens/${id}`, { method: 'DELETE' }),
  /** A person's tokens, for whoever may edit them (staff.manage). */
  staffAccessTokens: (staffId: string) => request<{ tokens: AccessToken[] }>(`/staff/${staffId}/tokens`),
  revokeStaffAccessToken: (staffId: string, id: string) =>
    request<void>(`/staff/${staffId}/tokens/${id}`, { method: 'DELETE' }),

  /** The organisation's directory connection, or `null` when none has been saved
   *  yet. `organisation` is for the installation's owner, who belongs to none and
   *  names which one; everybody else sees their own and the parameter is
   *  ignored. */
  directory: (organisation?: string) =>
    request<{ directory: DirectorySettings | null }>(
      organisation ? `/directory?organisation=${encodeURIComponent(organisation)}` : '/directory',
    ),

  /** Saves the connection. `bindPassword` absent keeps whatever is stored; `""`
   *  clears it; anything else replaces it. Only the installation's owner may
   *  call this — an organisation's own administrator may read the connection but
   *  not change it, because whoever controls it can sign in as every directory
   *  account in the organisation. */
  saveDirectory: (body: {
    organisation?: string
    url: string
    startTls: boolean
    caPem?: string
    bindDn?: string
    userTemplate?: string
    baseDn: string
    userFilter: string
    displayNameAttr: string
    groupAttr: string
    nestedGroups: boolean
    createAccounts: boolean
    offlineGraceHours: number
    enabled: boolean
    bindPassword?: string
    fallbackUrls?: string[]
    groupBaseDn?: string
    domains?: string[]
    connectTimeoutSeconds?: number
    timeoutSeconds?: number
    signInTries?: number
    syncMinutes?: number
  }) => request<{ directory: DirectorySettings }>('/directory', { method: 'PUT', body }),

  /** Tries a username and password against the saved directory. Creates and
   *  changes no account: it only answers what a sign-in would. */
  testDirectory: (organisation: string | undefined, username: string, password: string) =>
    request<DirectoryTestResult>('/directory/test', {
      method: 'POST',
      body: { ...(organisation ? { organisation } : {}), username, password },
    }),

  directoryMappings: (organisation?: string) =>
    request<{ mappings: GroupMapping[] }>(
      organisation ? `/directory/mappings?organisation=${encodeURIComponent(organisation)}` : '/directory/mappings',
    ),

  /** Replaces the organisation's whole list. A row naming a role the caller may
   *  not give must still be sent back unchanged — the server carries such rows
   *  over and refuses a save that drops or edits one. */
  saveDirectoryMappings: (
    organisation: string | undefined,
    mappings: Array<{ group: string; roleId?: string; branchId?: string }>,
  ) =>
    request<{ mappings: GroupMapping[] }>('/directory/mappings', {
      method: 'PUT',
      body: { ...(organisation ? { organisation } : {}), mappings },
    }),

  /** The report schedules this person may manage — their branches', and the
   *  network's where they see every branch — with the organisation's allowed
   *  recipient domains and whether a network letter may be offered. */
  reportSchedules: () => request<ScheduleList>('/report-schedules'),

  /** A new schedule. Saving never sends: the first letter is the next one due. */
  createReportSchedule: (body: ScheduleDraft) =>
    request<{ schedule: ReportSchedule }>('/report-schedules', { method: 'POST', body }),

  /** Changes a schedule. Its branch is fixed at creation; the server ignores a new one. */
  updateReportSchedule: (id: string, body: ScheduleDraft) =>
    request<{ schedule: ReportSchedule }>(`/report-schedules/${id}`, { method: 'PUT', body }),

  deleteReportSchedule: (id: string) =>
    request<Record<string, never>>(`/report-schedules/${id}`, { method: 'DELETE' }),

  /** The saved Reports views this person sees: their own and the organisation's shared ones. */
  reportViews: (organisation?: string) =>
    request<{ views: ReportView[] }>('/report-views' + (organisation ? `?organisation=${encodeURIComponent(organisation)}` : '')),

  saveReportView: (body: ViewDraft) => request<{ view: ReportView }>('/report-views', { method: 'POST', body }),

  /** Renames, shares or changes a view of one's own; updatedAt is the version it was changed from. */
  updateReportView: (id: string, body: { name: string; shared: boolean; spec?: ViewDraft['spec']; updatedAt?: string; description?: string }) =>
    request<{ view: ReportView }>(`/report-views/${id}`, { method: 'PUT', body }),

  /** The organisation's pivot reports, searched, sorted and a page at a time. */
  pivotReports: (q: { q: string; sort: 'updated' | 'created' | 'name'; page: number; pageSize: number; organisation?: string }, signal?: AbortSignal) => {
    const p = new URLSearchParams({ kind: 'pivot', q: q.q, sort: q.sort, page: String(q.page), pageSize: String(q.pageSize) })
    if (q.organisation) p.set('organisation', q.organisation)
    return request<{ views: ReportView[]; count: number }>(`/report-views?${p}`, { signal })
  },

  reportView: (id: string) => request<{ view: ReportView }>(`/report-views/${id}`),

  /** A copy of a report, the caller's own, with the layout on screen when given. */
  duplicateReportView: (id: string, spec?: ViewDraft['spec']) =>
    request<{ view: ReportView }>(`/report-views/${id}/duplicate`, { method: 'POST', body: spec ? { spec } : {} }),

  pivotFields: () => request<PivotFields>('/reports/pivot/fields'),

  pivotRun: (branches: string[], spec: PivotSpec, signal?: AbortSignal) => request<PivotResult>(`/reports/pivot?${pivotQuery(branches, spec)}`, { signal }),

  pivotValues: (branches: string[], spec: PivotSpec, field: string, signal?: AbortSignal) =>
    request<{ values: string[] }>(`/reports/pivot/values?${pivotQuery(branches, spec)}&field=${encodeURIComponent(field)}`, { signal }),

  /** The address of a pivot's file, which the browser downloads as it is. */
  pivotFile: (branches: string[], spec: PivotSpec, title: string, kind: 'xlsx' | 'csv') =>
    `${BASE}/reports/pivot.${kind}?${pivotQuery(branches, spec)}&title=${encodeURIComponent(title)}`,

  deleteReportView: (id: string) => request<Record<string, never>>(`/report-views/${id}`, { method: 'DELETE' }),

  /** Becomes the owner of a view whose owner left; its letters go on unchanged. */
  takeOverReportView: (id: string) => request<{ view: ReportView }>(`/report-views/${id}/take-over`, { method: 'POST' }),

  /** Queues the latest complete period at once, and names it. A second click in
   *  the same minute is the same letter. */
  sendReportNow: (id: string) =>
    request<{ periodFrom: string; periodTo: string }>(`/report-schedules/${id}/send`, { method: 'POST' }),

  /** The organisation's mail server, or `null` when none is saved. The password
   *  never comes back, only whether one is stored. The owner's alone, and
   *  `organisation` names which one, as on Directory. */
  mailServer: (organisation?: string) =>
    request<{ mailServer: MailServerSettings | null }>(
      organisation ? `/mail?organisation=${encodeURIComponent(organisation)}` : '/mail',
    ),

  /** Saves the mail server. `password` absent keeps what is stored; `""` clears
   *  it. A change of server, port, security, authority or username needs it again. */
  saveMailServer: (body: MailServerDraft & { organisation?: string; password?: string }) =>
    request<{ mailServer: MailServerSettings }>('/mail', { method: 'PUT', body }),

  /** Sends one letter now and answers with what the relay said. The address is
   *  not held to the allowed domains, but the answer says when it is outside them. */
  testMailServer: (organisation: string | undefined, to: string) =>
    request<{ sent: true; outsideAllowedDomains?: boolean }>('/mail/test', {
      method: 'POST',
      body: { ...(organisation ? { organisation } : {}), to },
    }),

  /** The organisation's webhook endpoints, and every event one may ask for.
   *  `organisation` is for the installation's owner, as on Directory. */
  webhooks: (organisation?: string) =>
    request<{ webhooks: Webhook[]; events: string[] }>(
      organisation ? `/webhooks?organisation=${encodeURIComponent(organisation)}` : '/webhooks',
    ),

  /** Creates an endpoint. The answer carries its secret, the only time it is sent. */
  createWebhook: (
    organisation: string | undefined,
    body: { name: string; url: string; events: string[]; branchIds: string[]; active: boolean },
  ) =>
    request<{ webhook: Webhook; secret: string }>('/webhooks', {
      method: 'POST',
      body: { ...(organisation ? { organisation } : {}), ...body },
    }),

  updateWebhook: (id: string, body: { name: string; url: string; events: string[]; branchIds: string[]; active: boolean }) =>
    request<{ webhook: Webhook }>(`/webhooks/${id}`, { method: 'PUT', body }),

  deleteWebhook: (id: string) => request<void>(`/webhooks/${id}`, { method: 'DELETE' }),

  /** Replaces the secret; the old one stops working at once. */
  newWebhookSecret: (id: string) => request<{ secret: string }>(`/webhooks/${id}/secret`, { method: 'POST', body: {} }),

  /** The organisation's data feed for its own BI. `organisation` is for the
   *  installation's owner, as on Webhooks; anybody else reads their own. */
  reportingFeed: (organisation?: string) =>
    request<FeedAnswer>(
      organisation ? `/reporting-feed?organisation=${encodeURIComponent(organisation)}` : '/reporting-feed',
    ),

  /** Turn on, Turn off, New password, Check or Feed key. A password or key is in
   *  this answer and in no later one. */
  feedAction: (action: 'enable' | 'disable' | 'password' | 'check' | 'key', organisation?: string) =>
    request<FeedAnswer>(`/reporting-feed/${action}`, {
      method: 'POST',
      body: organisation ? { organisation } : {},
    }),

  /** An organisation's connectors, on and off, by name. `organisation` is for
   *  the installation's owner, as on Webhooks. */
  connectors: (organisation?: string) =>
    request<{ connectors: Connector[] }>(
      organisation ? `/connectors?organisation=${encodeURIComponent(organisation)}` : '/connectors',
    ),

  /** Registers a connector. The secret, when one is given, is sealed on the
   *  server and never sent back. */
  createConnector: (organisation: string | undefined, body: ConnectorBody & { secret?: string }) =>
    request<{ connector: Connector }>('/connectors', {
      method: 'POST',
      body: { ...(organisation ? { organisation } : {}), ...body },
    }),

  /** Everything but the secret, which setConnectorSecret changes. */
  /** Everything but the secret. A new host clears the secret, and the answer
   *  says so with secretCleared. */
  updateConnector: (id: string, body: ConnectorBody) =>
    request<{ connector: Connector; secretCleared: boolean }>(`/connectors/${id}`, { method: 'PUT', body }),

  /** Sets, replaces or, with '', clears a connector's secret. */
  setConnectorSecret: (id: string, secret: string) =>
    request<{ connector: Connector }>(`/connectors/${id}/secret`, { method: 'PUT', body: { secret } }),

  /** Deletes a connector and its call log. */
  deleteConnector: (id: string) => request<void>(`/connectors/${id}`, { method: 'DELETE' }),

  /** A connector's last fifty calls, newest first. */
  connectorCalls: (id: string) => request<{ calls: ConnectorCall[] }>(`/connectors/${id}/calls`),

  testWebhook: (id: string) => request<WebhookTestResult>(`/webhooks/${id}/test`, { method: 'POST', body: {} }),

  /** What the outbox tried to send, newest first, on every channel. */
  deliveries: (organisation: string | undefined, filter: { channel?: string; status?: string }) => {
    const q = new URLSearchParams()
    if (organisation) q.set('organisation', organisation)
    if (filter.channel) q.set('channel', filter.channel)
    if (filter.status) q.set('status', filter.status)
    const s = q.toString()
    // owner: the server's own answer to whether this person sees webhooks and may send again.
    return request<{ deliveries: Delivery[]; owner?: boolean }>(s ? `/deliveries?${s}` : '/deliveries')
  },

  resendDelivery: (organisation: string | undefined, id: string) =>
    request<void>(`/deliveries/${id}/resend`, { method: 'POST', body: organisation ? { organisation } : {} }),

  // Texting customers about their visit: the organisation's words and moments,
  // and the installation owner's gateway. See internal/api/notifications.go.
  notifications: (organisation?: string) =>
    request<NotificationsView>(
      organisation ? `/notifications?organisation=${encodeURIComponent(organisation)}` : '/notifications',
    ),

  saveNotifications: (settings: NotificationSettings, organisation?: string) =>
    request<NotificationsView>('/notifications', { method: 'PUT', body: { organisation, settings } }),

  /** secret: absent keeps what is stored, '' clears it. */
  saveSMSGateway: (gateway: SMSGateway, secret: string | undefined, organisation?: string) =>
    request<NotificationsView>('/notifications/gateway', { method: 'PUT', body: { organisation, gateway, secret } }),

  /** Forgets the gateway and its secret, and gives up every text still waiting. Not licence-locked. */
  removeSMSGateway: (organisation?: string) =>
    request<NotificationsView>(
      '/notifications/gateway' + (organisation ? '?organisation=' + encodeURIComponent(organisation) : ''),
      { method: 'DELETE' },
    ),

  testSMS: (to: string, organisation?: string) =>
    request<{ delivered: boolean; answer?: string }>('/notifications/test', {
      method: 'POST',
      body: { organisation, to },
    }),

  /** A branch's queue alert levels, and the queues on its floor that may have one;
   *  mailReady is whether their letters can be sent. See internal/api/queuealerts.go. */
  queueAlerts: (branchId: string) =>
    request<{ alerts: QueueAlertSetting[]; queues: { id: string; code: string; name: Record<string, string> }[]; mailReady: boolean }>(
      `/branches/${branchId}/queue-alerts`,
    ),

  saveQueueAlerts: (branchId: string, alerts: QueueAlertSetting[]) =>
    request<{ alerts: QueueAlertSetting[] }>(`/branches/${branchId}/queue-alerts`, {
      method: 'PUT',
      body: { alerts },
    }),

  // Layouts. The screens ask for one document; the editor works with the whole
  // record — draft, published, version and history.
  branchLayout: (branchId: string, kind: LayoutKind) =>
    request<{
      document: LayoutDocument
      version: number
      builtIn?: boolean
      layoutId?: string
      byService?: SlipsByService
      /** What the playlists are playing now, when any has times — so a board can
       *  tell a change of time from a new version. The board itself compares the
       *  items it was sent (layout/playlist.ts drawnKey), because this key changes
       *  at midnight for a late-night time written as two, with the same list. */
      playing?: string
      /** When that next changes, in UTC. The board asks again then. Absent when
       *  nothing on the design ever hands over. */
      changesAt?: string
    }>(`/branches/${branchId}/layout/${kind}`).then((r) => ({ ...r, document: unpackCalled(r.document) })),

  /** The design an addressed machine should be drawing.
   *
   *  The kind is not in the address: the server takes it from what the unit is, so a
   *  hall board cannot ask for the kiosk's design. This route needs no session,
   *  which is the whole point — the one above does, and an unattended screen calling
   *  it got a 401 and painted nothing. */
  screenLayout: (branchCode: string, unitCode: string, held?: string) =>
    request<{
      document: LayoutDocument
      version: number
      builtIn?: boolean
      layoutId?: string
      /** What the playlists are playing now, when any has times — so a board can
       *  tell a change of time from a new version. The board itself compares the
       *  items it was sent (layout/playlist.ts drawnKey), because this key changes
       *  at midnight for a late-night time written as two, with the same list. */
      playing?: string
      /** When that next changes, in UTC. The board asks again then. Absent when
       *  nothing on the design ever hands over. */
      changesAt?: string
      /** A kiosk is also told what its paper looks like, resolved for this
       *  machine. Sent with the screen's own design rather than fetched
       *  separately: it is needed before the first customer touches anything, and
       *  a second request is a second thing that can be missing when paper is
       *  due. Absent for a hall display, which prints nothing.
       *  byService is the services that print a slip of their own on this
       *  machine. */
      ticket?: {
        document: LayoutDocument
        version: number
        builtIn?: boolean
        byService?: SlipsByService
      }
      /** A kiosk only: whether its branch has bookings still to arrive today, which is
       *  whether it offers "I have a booking" at all. */
      bookings?: boolean
      /** The screen app placed on this machine, when one is. Sent beside the
       *  document rather than instead of it: the document is still what the
       *  machine draws when the app cannot run, and the kiosk still prints its
       *  slip from `ticket`. */
      app?: ScreenAppAnswer
      /** The files this screen should hold now and in the next day, nearest first; absent on a server fault. */
      prefetch?: PrefetchFile[]
      /** Names this answer; sent back as `held`, an unchanged one comes back as undefined. */
      etag?: string
    } | undefined>(
      `/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/layout`,
      held ? { headers: { 'If-None-Match': `"${held}"` } } : {},
    ).then((r) => (r ? { ...r, document: unpackCalled(r.document) } : r)),

  layouts: (organisation?: string) =>
    request<{ layouts: StoredLayout[]; undesigned: LayoutKind[]; maxElements: number; maxElementsByKind: Record<LayoutKind, number> }>(
      organisation ? `/layouts?organisation=${encodeURIComponent(organisation)}` : '/layouts',
    ).then((r) => ({ ...r, layouts: r.layouts.map(unpackStored) })),

  /** The designs a screen can be started from, by kind. Each carries its whole
   *  document, so the gallery draws a real thumbnail rather than a picture of it. */
  layoutTemplates: () =>
    request<{ templates: Record<LayoutKind, LayoutTemplate[]> }>('/layouts/templates').then((r) => ({
      templates: Object.fromEntries(
        Object.entries(r.templates).map(([kind, list]) => [kind, list.map((tpl) => ({ ...tpl, document: unpackCalled(tpl.document) }))]),
      ) as Record<LayoutKind, LayoutTemplate[]>,
    })),

  // `name` is not optional in practice for a ticket: the server refuses one without
  // it, because a printed slip belongs to the organisation rather than to a branch
  // and a name is the only thing that tells two of them apart. It is typed optional
  // because a board and a kiosk are named after the branch or machine they belong to
  // and do not need one.
  //
  // It was missing from this body until 22 September 2026, while the picker that
  // calls it had collected a name all along — so every attempt to create a ticket
  // design answered 422 with a message naming exactly what was wrong, and nobody
  // could act on it, because the field the message asked for was being dropped here
  // rather than left empty by the person. TypeScript did not object: the caller
  // passes a variable rather than an object literal, so excess-property checking
  // never ran on it.
  //
  // A design belongs to no place: units choose it in their settings (equipment spec §6).
  createLayout: (of: {
    kind: LayoutKind
    template?: string
    name?: string
    default?: boolean
    /** Required for a kiosk design, refused for any other kind. */
    operationProfileId?: string
  }) =>
    request<{ layout: StoredLayout }>('/layouts', {
      method: 'POST',
      body: {
        kind: of.kind,
        ...(of.template ? { template: of.template } : {}),
        ...(of.name ? { name: of.name } : {}),
        ...(of.default !== undefined ? { default: of.default } : {}),
        ...(of.operationProfileId ? { operationProfileId: of.operationProfileId } : {}),
        // The server reads a create's organisation from the body only.
        ...(contextOrganisation() ? { organisation: contextOrganisation() } : {}),
      },
    }).then((r) => ({ layout: unpackStored(r.layout) })),

  layout: (layoutId: string) =>
    request<{ layout: StoredLayout; changed: boolean }>(`/layouts/${layoutId}`).then((r) => ({ ...r, layout: unpackStored(r.layout) })),

  /** A new unassigned draft holding the design's latest document. A taken name
   *  comes back numbered: "X (copy)" → "X (copy 2)". */
  duplicateLayout: (layoutId: string, name?: string) =>
    request<{ layout: StoredLayout }>(`/layouts/${layoutId}/duplicate`, {
      method: 'POST',
      body: name ? { name } : {},
    }).then((r) => ({ layout: unpackStored(r.layout) })),

  /** Refused (409 LAYOUT_IN_USE) while the design is the default or anything
   *  chooses it. */
  deleteLayout: (layoutId: string) =>
    request<{ deleted: boolean }>(`/layouts/${layoutId}`, { method: 'DELETE' }),

  /** base is the draft revision the edit started from; a save on top of a newer
   *  one is refused (409 DRAFT_MOVED). Left out, the save lands whatever is there. */
  saveDraft: (layoutId: string, document: LayoutDocument, base?: number, opts: { keepalive?: boolean } = {}) =>
    request<{ saved: boolean; draftRevision?: number }>(
      `/layouts/${layoutId}/draft${base !== undefined ? `?base=${base}` : ''}`,
      // A save that never answers becomes a refusal, so leaving a design cannot hang on it.
      { method: 'PUT', body: packCalled(document), timeoutMs: DRAFT_SAVE_TIMEOUT_MS, keepalive: opts.keepalive },
    ),

  publishLayout: (layoutId: string, note?: string) =>
    request<{ version: number }>(`/layouts/${layoutId}/publish`, {
      method: 'POST',
      body: note ? { note } : {},
    }),

  layoutVersions: (layoutId: string) =>
    request<{ versions: LayoutVersion[] }>(`/layouts/${layoutId}/versions`).then((r) => ({
      ...r,
      versions: r.versions.map((v) => ({ ...v, document: unpackCalled(v.document) })),
    })),

  restoreLayout: (layoutId: string, version: number) =>
    request<{ layout: LayoutDocument; draftRevision?: number }>(`/layouts/${layoutId}/restore`, {
      method: 'POST',
      body: { version },
    }).then((r) => ({ ...r, layout: unpackCalled(r.layout) })),

  assets: () => request<{ assets: LayoutAsset[] }>('/assets'),
  designFilms: () => request<{ films: DesignFilm[]; freeBytes?: number }>('/assets/films'),
  /** versions: the old versions naming the picture, confirmed by the person. */
  deletePicture: (id: string, versions = 0) =>
    request<void>(`/assets/${encodeURIComponent(id)}?versions=${versions}`, { method: 'DELETE' }),
  renameLayout: (layoutId: string, name: string) =>
    request<{ layout: StoredLayout }>(`/layouts/${layoutId}`, { method: 'PATCH', body: { name } }).then((r) => ({ layout: unpackStored(r.layout) })),
  /** versions: the old versions naming the film, confirmed by the person. */
  deleteFilm: (id: string, versions: number) =>
    request<void>(`/assets/${encodeURIComponent(id)}?versions=${versions}`, { method: 'DELETE' }),

  /** The URL a picture is served from. Used by the renderer and by the editor. */
  assetURL: (assetId: string) => `${BASE}/assets/${assetId}/content`,

  /** The URL an unattended screen fetches a picture from.
   *
   *  A different route from the one above, and it has to be: that one needs a
   *  session and a machine identified by its address has none, so a hall display
   *  with a logo on it fetched a 401 and drew a gap. This one is scoped to the
   *  organisation the address belongs to. */
  screenAssetURL: (branchCode: string, unitCode: string) => (assetId: string) =>
    `${BASE}/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/asset/${encodeURIComponent(assetId)}`,

  /** `picture` when the upload is for a picture element: the server then refuses
   *  a film before storing any of it, rather than keeping a file nothing can show.
   *  `slip` is a picture for a printed ticket, held to the slip's smaller cap. */
  uploadAsset: async (file: File, kind?: 'picture' | 'slip'): Promise<LayoutAsset> => {
    // Refused here as the server would, without sending twenty megabytes first.
    if (kind === 'picture' && pictureTooBig(file.size)) {
      const mb = String(MAX_PICTURE_BYTES >> 20)
      throw new ApiError(400, 'UPLOAD_TOO_LARGE', 'UPLOAD_TOO_LARGE', undefined, { mb })
    }
    // Multipart rather than JSON: a base64 image in a JSON body is a third larger
    // and has to be held in memory twice on the way in.
    const body = new FormData()
    // Before the file, so the server knows what is asked for before it reads it.
    if (kind) body.append('kind', kind)
    body.append('file', file)
    return (await sendForm<{ asset: LayoutAsset }>('/assets', body)).asset
  },

  /** The organisation's uploaded sounds, which a machine may play as its chime. */
  sounds: () => request<{ sounds: LayoutAsset[] }>('/sounds'),
  uploadSound: async (file: File): Promise<LayoutAsset> => {
    const body = new FormData()
    body.append('file', file)
    return (await sendForm<{ asset: LayoutAsset }>('/sounds', body)).asset
  },

  signageMedia: () => request<{ media: SignageMedia[]; usedBytes: number; limitBytes: number }>('/signage/media'),
  uploadSignageMedia: (
    file: File,
    extra: { name?: string; durationMs?: number; poster?: Blob; onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ) => {
    const body = new FormData()
    if (extra.name) body.append('name', extra.name)
    if (extra.durationMs) body.append('durationMs', String(Math.round(extra.durationMs)))
    body.append('file', file)
    if (extra.poster) body.append('poster', extra.poster, 'poster.jpg')
    // duplicate: the same bytes were already in the library; nothing was stored.
    if (extra.onProgress) return sendFormWithProgress<{ media: SignageMedia; duplicate?: boolean }>('/signage/media', body, extra.onProgress, extra.signal)
    return sendForm<{ media: SignageMedia; duplicate?: boolean }>('/signage/media', body)
  },
  renameSignageMedia: (id: string, name: string) =>
    request<{ media: SignageMedia }>(`/signage/media/${id}`, { method: 'PATCH', body: { name } }),
  deleteSignageMedia: (id: string, versions = 0) =>
    request<void>(`/signage/media/${id}?versions=${versions}`, { method: 'DELETE' }),
  playlists: () => request<{ playlists: PlaylistSummary[] }>('/signage/playlists'),
  /** The same list, for a designer choosing a media area's playlist (layout.manage). */
  designPlaylists: () => request<{ playlists: PlaylistSummary[] }>('/layouts/playlists'),
  /** A saved playlist as a screen plays it, for the editor's Preview (layout.manage). */
  designPlaylistPlayed: (id: string) => request<{ items: MediaItem[]; ticker: LayoutText[] }>(`/layouts/played?playlist=${encodeURIComponent(id)}`),
  playlist: (id: string) => request<{ playlist: Playlist }>(`/signage/playlists/${id}`),
  createPlaylist: (body: { name: string; entries: PlaylistEntry[]; ticker: TickerLine[] }) =>
    request<{ playlist: Playlist }>('/signage/playlists', { method: 'POST', body }),
  savePlaylist: (id: string, body: { name: string; version: number; entries: PlaylistEntry[]; ticker: TickerLine[] }) =>
    request<{ playlist: Playlist }>(`/signage/playlists/${id}`, { method: 'PUT', body }),
  deletePlaylist: (id: string) => request<void>(`/signage/playlists/${id}`, { method: 'DELETE' }),
  campaigns: () => request<{ campaigns: Campaign[]; branches: BranchSeat[]; today: string }>('/signage/campaigns'),
  createCampaign: (body: CampaignBody) => request<{ campaign: Campaign }>('/signage/campaigns', { method: 'POST', body }),
  saveCampaign: (id: string, body: CampaignBody) =>
    request<{ campaign: Campaign }>(`/signage/campaigns/${id}`, { method: 'PUT', body }),
  deleteCampaign: (id: string) => request<void>(`/signage/campaigns/${id}`, { method: 'DELETE' }),
  /** at: a branch wall time (YYYY-MM-DDTHH:MM) within a year; empty is now. */
  signageScreens: () => request<{ screens: SignageScreen[] }>('/signage/screens'),
  signageNow: (branchId: string, place: SignagePlace, at = '') =>
    request<SignageNow>(`/signage/now?branch=${encodeURIComponent(branchId)}&place=${place}${at ? `&at=${encodeURIComponent(at)}` : ''}`),

  branches: () => request<{ branches: Branch[] }>('/branches'),

  snapshot: (branchId: string) => request<Snapshot>(`/branches/${branchId}/snapshot`),

  /** Reset now: closes the day and restarts today's numbers (branch.manage). */
  resetDay: (branchId: string) => request<{ closed: number }>(`/branches/${branchId}/reset`, { method: 'POST', body: {} }),

  services: (branchId: string) => request<{ services: Service[] }>(`/branches/${branchId}/services`),

  /** What a customer taking a standard ticket now would be told, per queue, and
   *  where each waiting ticket stands: the server's estimate, never worked out here. */
  estimates: (branchId: string, signal?: AbortSignal) =>
    request<{ at: string; queues: QueueEstimate[]; tickets?: TicketEstimate[] }>(`/branches/${branchId}/estimates`, { signal }),

  /** The waits customers were told against the waits they had, per day and queue. */
  estimateAccuracy: (branchId: string, from: string, to: string, signal?: AbortSignal) =>
    request<EstimateAccuracy>(`/branches/${branchId}/report/estimates?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { signal }),

  profiles: (branchId: string) => request<{ profiles: WorkProfile[] }>(`/branches/${branchId}/profiles`),

  findByNumber: (branchId: string, number: string) =>
    request<{ tickets: Ticket[]; position: number; estimatedWaitSec: number; estimate?: Estimate | null }>(
      `/branches/${branchId}/tickets?number=${encodeURIComponent(number)}`,
    ),

  /** Every ticket of one business day, closed and voided ones included — the
   *  list reception reads the day from (handleFindTickets with businessDate). */
  ticketsForDay: (branchId: string, businessDate: string) =>
    request<{ tickets: Ticket[] | null }>(
      `/branches/${branchId}/tickets?businessDate=${encodeURIComponent(businessDate)}`,
    ),

  issueTicket: (
    branchId: string,
    body: {
      serviceId: string
      priority?: string
      source?: string
      language?: string
      /** The remark this visit carries to the desk, under the same name the
       *  ticket's own representation and PATCH /tickets/{id}/note use. */
      note?: string
      /** The other services this customer needs, in the order they will be
       *  taken. serviceId is still the arrival: it alone decides the queue, the
       *  priority, the service-level target and what prints on the slip. These
       *  become the visit's plan, and the server refuses one that does not route
       *  here, one already on the visit, or more than a visit may carry. */
      alsoServiceIds?: string[]
      /** Values in what the organisation has declared a ticket may carry, checked
       *  by the server (checkFields) before the ticket exists. */
      fields?: Record<string, string>
    },
  ) =>
    request<{
      ticket: Ticket
      position: number
      estimatedWaitSec: number
      /** The server's estimate with its range and flags; null when not waiting. */
      estimate?: Estimate | null
      /** The slip's validity, RFC 3339 in the branch's offset; absent prints nothing. */
      validUntil?: string
    }>(`/branches/${branchId}/tickets`, {
      method: 'POST',
      body,
      idempotent: true,
    }),

  /** A branch's bookings for one day (today by default), or over range (from..to,
   *  both YYYY-MM-DD, at most 31 days; businessDate is then from), with the check-in window. */
  appointments: (branchId: string, date?: string, range?: { from: string; to: string }) =>
    request<{ businessDate: string; appointments: Appointment[]; checkIn: CheckInRules }>(
      `/branches/${branchId}/appointments${
        range
          ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
          : date
            ? `?date=${encodeURIComponent(date)}`
            : ''
      }`,
    ),

  /** Arrive a booked customer, by the row pressed or the code they hold. The
   *  booking comes back projected (viewOf in the Go, withReference false): no
   *  values, and no reference — a request by code proves nothing about who is
   *  asking for whose booking. Outside the check-in window it is refused
   *  CHECKIN_TOO_EARLY or CHECKIN_TOO_LATE; past it, regular issues an ordinary
   *  ticket and the booking comes back missed, naming that ticket. */
  arrive: (branchId: string, body: { appointmentId?: string; code?: string; language?: string; regular?: boolean }) =>
    request<{ ticket: Ticket; position: number; estimatedWaitSec: number; estimate?: Estimate | null; appointment: BookingView }>(
      `/branches/${branchId}/appointments/arrive`,
      { method: 'POST', body, idempotent: true },
    ),

  /** Bring a file of bookings in; with dryRun, say what that would do and write nothing. */
  importAppointments: (branchId: string, csv: string, dryRun: boolean) =>
    request<ImportAnswer>(`/branches/${branchId}/appointments/import`, {
      method: 'POST',
      body: { csv, dryRun },
    }),

  /** Cancel an imported booking by its reference; reason (one line, ≤200) is kept as cancelReason. */
  cancelAppointment: (branchId: string, reference: string, reason?: string) =>
    request<{ appointment: BookingView }>(
      `/branches/${branchId}/appointments/${encodeURIComponent(reference)}`,
      reason ? { method: 'DELETE', body: { reason } } : { method: 'DELETE' },
    ),

  /** Create or change one booking by its reference: the import, for one row
   *  (PUT /branches/{id}/appointments/{reference}, handleSaveAppointment). A row
   *  the server will not take is thrown as APPOINTMENT_INVALID or
   *  APPOINTMENT_REFUSED with the import's reason; lib/appointmentImport's
   *  bookingRefusal reads it. */
  saveAppointment: (
    branchId: string,
    reference: string,
    body: { service: string; date: string; time: string; code?: string; fields?: Record<string, string> },
  ) =>
    request<{ appointment?: BookingView }>(`/branches/${branchId}/appointments/${encodeURIComponent(reference)}`, {
      method: 'PUT',
      body,
    }),

  appointmentPolicy: () => request<{ keepDays: number }>('/appointments/policy'),

  setAppointmentPolicy: (keepDays: number) =>
    request<{ keepDays: number }>('/appointments/policy', { method: 'PUT', body: { keepDays } }),

  // Online booking: Administration -> Online booking (Branches, Rules, API keys).
  bookingPolicy: () => request<BookingPolicy>('/booking/policy'),
  saveBookingPolicy: (p: BookingPolicy) => request<BookingPolicy>('/booking/policy', { method: 'PUT', body: p }),
  bookingBranch: (branchId: string) => request<BookingBranchView>(`/booking/branches/${branchId}`),
  saveBookingBranch: (branchId: string, s: BookingBranchSettings) =>
    request<BookingBranchSettings>(`/booking/branches/${branchId}`, { method: 'PUT', body: s }),
  saveBookingService: (
    branchId: string,
    row: Pick<BookingServiceRow, 'serviceId' | 'enabled' | 'slotMin' | 'perSlot'> &
      Partial<Pick<BookingServiceRow, 'weekdays' | 'dailyMax' | 'daysAhead' | 'minNoticeMin'>>,
  ) =>
    request<BookingServiceRow>(`/booking/branches/${branchId}/services/${row.serviceId}`, {
      method: 'PUT',
      body: { enabled: row.enabled, slotMin: row.slotMin, perSlot: row.perSlot },
    }),
  addClosedDay: (branchId: string, day: string, label: string) =>
    request<ClosedDay>(`/booking/branches/${branchId}/closed/${day}`, { method: 'PUT', body: { label } }),
  removeClosedDay: (branchId: string, day: string) =>
    request<void>(`/booking/branches/${branchId}/closed/${day}`, { method: 'DELETE' }),
  bookingKeys: () => request<{ keys: BookingKey[] }>('/booking/keys'),
  createBookingKey: (body: { name: string; trusted: boolean; perMinute: number }) =>
    request<{ key: BookingKey; secret: string }>('/booking/keys', { method: 'POST', body }),
  updateBookingKey: (id: string, body: { name?: string; perMinute?: number; active?: boolean }) =>
    request<BookingKey>(`/booking/keys/${id}`, { method: 'PATCH', body }),
  deleteBookingKey: (id: string) => request<void>(`/booking/keys/${id}`, { method: 'DELETE' }),

  // Reception's Book, and the Appointments screen's online rows. finMode and
  // channels ride along because reception has no route onto the policy itself.
  bookingSlots: (branchId: string, serviceId: string, date: string) =>
    request<{ date: string; slots: BookingSlot[]; finMode: FinMode; channels: BookingChannel[]; fields?: BookingFormFields }>(
      `/branches/${branchId}/booking/slots?service=${encodeURIComponent(serviceId)}&date=${encodeURIComponent(date)}`,
    ),
  /** A booked customer's own name, for the ticket they arrived as; '' for none. */
  bookedName: (branchId: string, ticketId: string) =>
    request<{ name: string }>(`/branches/${branchId}/tickets/${encodeURIComponent(ticketId)}/booked-name`),
  /** Today's booked names by ticket id, for Reception's list. Names only. */
  bookedNames: (branchId: string) => request<{ names: Record<string, string> }>(`/branches/${branchId}/booked-names`),
  /** Branches the person may book at, with booking on, and each one's bookable services. */
  appointmentCatalogue: () => request<{ branches: CatalogueEntry[] }>('/appointments/catalogue'),
  /** The staff booking form: each field's mode, own fields, channels and rules.
   *  For ticket.issue or appointment.manage. */
  bookingForm: (branchId: string) => request<BookingForm>(`/branches/${branchId}/booking/form`),
  bookingDay: (branchId: string, date: string) =>
    request<{ date: string; offered: number; booked: number }>(`/branches/${branchId}/booking/day?date=${encodeURIComponent(date)}`),
  bookAtDesk: (
    branchId: string,
    body: {
      service: string
      start: string
      name: string
      fin: string
      channel: BookingChannel
      contact: string
      language: string
      /** The contact the code does not go to, when the form asks it. */
      mobile?: string
      email?: string
      /** Own fields by key. */
      fields?: Record<string, string>
    },
  ) => request<{ appointment: Appointment }>(`/branches/${branchId}/booking`, { method: 'POST', body, idempotent: true }),
  /** Cancel an online or reception booking; reason (one line, ≤200) is kept as cancelReason. */
  cancelBooking: (branchId: string, id: string, reason?: string) =>
    request<{ appointment: Appointment }>(
      `/branches/${branchId}/appointments/${id}/cancel`,
      reason ? { method: 'POST', body: { reason } } : { method: 'POST' },
    ),
  /** Move to start (RFC 3339); serviceId changes the service, checked as booking it would be. */
  moveBooking: (branchId: string, id: string, start: string, serviceId?: string) =>
    request<{ appointment: Appointment }>(`/branches/${branchId}/appointments/${id}/move`, {
      method: 'POST',
      body: serviceId ? { start, serviceId } : { start },
    }),
  /** The booking's manage link for the customer; '' where the organisation has no manage page. */
  bookingLink: (branchId: string, id: string) =>
    request<{ manageUrl: string }>(`/branches/${branchId}/appointments/${id}/link`),

  /** A visit's note history, oldest first. */
  ticketNotes: (ticketId: string) => request<{ notes: NoteEntry[] }>(`/tickets/${ticketId}/notes`),
  /** Adds an entry to an open visit's history; VISIT_OVER once it has finished. */
  addTicketNote: (ticketId: string, text: string) =>
    request<{ ticket: Ticket; note: NoteEntry }>(`/tickets/${ticketId}/notes`, { method: 'POST', body: { text } }),
  /** Edits one's own entry while the customer is still at one's desk (NOTE_LOCKED after). */
  editTicketNote: (ticketId: string, noteId: string, text: string) =>
    request<{ ticket: Ticket; note: NoteEntry }>(`/tickets/${ticketId}/notes/${noteId}`, { method: 'PATCH', body: { text } }),
  deleteTicketNote: (ticketId: string, noteId: string) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/notes/${noteId}`, { method: 'DELETE' }),
  /** A ticket's history, oldest first. */
  ticketEvents: (ticketId: string) => request<{ events: TicketHistoryEvent[] }>(`/tickets/${ticketId}/events`),
  /** Names the customer on an open visit; both empty clears it. */
  setTicketCustomer: (ticketId: string, name: string, phone: string) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/customer`, { method: 'PUT', body: { name, phone } }),
  /** The organisation's known customers by part of a name or a mobile. */
  searchCustomers: (branchId: string, q: string) =>
    request<{ customers: KnownCustomer[] }>(`/branches/${branchId}/customers?q=${encodeURIComponent(q)}`),

  /** A fresh follow link for the slip about to be printed. Replaces the ticket's
   *  previous link, so printing again revokes a lost slip's code. */
  followLink: (ticketId: string) =>
    request<{ followUrl?: string; validUntil?: string }>(`/tickets/${ticketId}/follow-link`, { method: 'POST' }),

  /** The customer's own ticket, by the token in their link. No session. */
  follow: (token: string) => request<FollowView>('/follow', { headers: { 'X-Follow-Token': token } }),
  /** The customer leaves the queue. Pressing twice answers the same. */
  leaveQueue: (token: string) =>
    request<FollowView>('/follow/leave', { method: 'POST', headers: { 'X-Follow-Token': token } }),
  /** The number to text, typed by the customer on the follow page. */
  followPhone: (token: string, phone: string) =>
    request<FollowView>('/follow/phone', { method: 'PUT', headers: { 'X-Follow-Token': token }, body: { phone } }),
  followPhoneStop: (token: string) =>
    request<FollowView>('/follow/phone', { method: 'DELETE', headers: { 'X-Follow-Token': token } }),

  /** What this ticket carries beyond its number. Only the keys in `changed` are
   *  written, from `fields` or cleared when `fields` lacks them; every other
   *  stored value is kept, so a number the customer withdrew while the drawer
   *  was open is not written back.
   *
   *  Never called without a map. Omitting the key entirely — or sending null —
   *  is the one body the route reads as "change nothing", so a caller that let
   *  `fields` go undefined would get a cheerful 200 and no effect. `Record` is
   *  not optional here for exactly that reason.
   *
   *  A value refused by its declaration comes back as one VALIDATION_FAILED
   *  whose detail names every fault at once, joined with "; ". lib/ticketFields
   *  takes it apart again so each sentence lands beside the box it is about. */
  setTicketFields: (ticketId: string, fields: Record<string, string>, changed: string[]) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/fields`, {
      method: 'PATCH',
      body: { fields, changed },
    }),

  /** Add one service to what a visit still owes the customer, or clear the lot.
   *
   *  Exactly one of the two, which is why this takes a union rather than a shape
   *  with two optional keys: the server refuses a body that says both and a body
   *  that says neither, and a caller that can express either of those is a caller
   *  that will eventually send one. There is no "remove one" — a plan with the
   *  wrong fourth entry is a plan to clear and enter again. */
  ticketPlan: (ticketId: string, body: { add: string } | { drop: true }) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/actions/plan`, {
      method: 'POST',
      body,
      idempotent: true,
    }),

  ticketAction: (ticketId: string, action: string, body: Record<string, unknown> = {}) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/actions/${action}`, {
      method: 'POST',
      body,
      idempotent: true,
    }),

  /** Record one delivered service. entryId is the intent's own id: a press repeated
   *  after a lost answer sends the same one, and the server records it once. */
  deliver: (ticketId: string, entryId: string, deliveredServiceId: string, outcomeCode?: string) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/actions/deliver`, {
      method: 'POST',
      body: { deliveredServiceId, entryId, ...(outcomeCode ? { outcomeCode } : {}) },
      idempotent: true,
    }),

  /** Put a set-aside customer back into a line.
   *
   *  Its own function rather than a ticketAction call with a body, because of what
   *  it deliberately does not send. Unpark keeps the ticket's existing queue when
   *  no queue is named, and that queue is the one the customer belongs in — they
   *  were taken out of it, not moved. A caller able to pass a queue here is a
   *  caller that will one day pass the wrong one, and the customer would reappear
   *  in a line nobody expects them in. */
  ticketUnpark: (ticketId: string) =>
    request<{ ticket: Ticket }>(`/tickets/${ticketId}/actions/unpark`, {
      method: 'POST',
      body: {},
      idempotent: true,
    }),

  openCounter: (counterId: string, workProfileId: string) =>
    request<{ session: { id: string } }>(`/counters/${counterId}/session`, {
      method: 'POST',
      body: { workProfileId },
    }),

  /** A break for a reason; it runs until the clerk ends it. */
  requestBreak: (counterId: string, reasonId: string) =>
    request<{ request: RequestView }>(`/counters/${counterId}/request`, {
      method: 'POST',
      body: { reasonId },
    }),

  closeCounter: (counterId: string) =>
    request<void>(`/counters/${counterId}/session`, { method: 'DELETE' }),

  // Only ever 'open' now: a pause is asked for (requestStatus); this is the
  // resume of a desk paused before requests existed.
  setCounterState: (counterId: string, state: 'open', reason = '') =>
    request<{ state: string }>(`/counters/${counterId}/state`, {
      method: 'POST',
      body: { state, reason },
    }),

  callNext: (counterId: string) =>
    request<{ ticket: Ticket }>(`/counters/${counterId}/call-next`, {
      method: 'POST',
      body: {},
      idempotent: true,
    }),

  /** Call next, or — with nobody this desk could call — have the next customer who
   *  arrives called here automatically. `ticket` is whoever was called now. */
  standBy: (counterId: string) =>
    request<{ ticket: Ticket | null; standingBy: boolean }>(`/counters/${counterId}/stand-by`, {
      method: 'POST',
      body: {},
      idempotent: true,
    }),

  stopStandingBy: (counterId: string) =>
    request<void>(`/counters/${counterId}/stand-by`, { method: 'DELETE' }),

  callSpecific: (counterId: string, ticketId: string, reason = '') =>
    request<{ ticket: Ticket }>(`/counters/${counterId}/call`, {
      method: 'POST',
      body: { ticketId, reason },
      idempotent: true,
    }),

  /** The operator's Ask: a break or a close, offered only while the desk has no
   *  customer. A kind the branch does not require approval for starts (or closes
   *  the desk) at once; otherwise the desk is paused and pending. */
  requestStatus: (counterId: string, kind: RequestKind) =>
    request<{ request: RequestView }>(`/counters/${counterId}/request`, {
      method: 'POST',
      body: { kind },
    }),

  /** The operator's Cancel: withdraws the desk's still undecided request. */
  cancelRequest: (counterId: string) =>
    request<void>(`/counters/${counterId}/request`, { method: 'DELETE' }),

  /** The operator's Back: ends the break running at this desk, no approval
   *  needed. */
  endBreak: (counterId: string) =>
    request<void>(`/counters/${counterId}/break/end`, { method: 'POST', body: {} }),

  /** A manager's Approve or Decline, from the Floor's Requests list. The
   *  branch is in the path and reached before the request is read, so another
   *  branch's request answers 404 rather than 403 (internal/api/deskrequests.go). */
  decideRequest: (branchId: string, requestId: string, approve: boolean, reason?: string) =>
    request<{ request: { id: string } }>(`/branches/${branchId}/requests/${requestId}/decision`, {
      method: 'POST',
      body: { approve, ...(reason ? { reason } : {}) },
    }),

  /** Serve the customer standing at the desk, who never took a ticket: one is
   *  issued, called here and started, and nothing is announced. */
  walkDirect: (branchId: string, counterId: string, serviceId: string) =>
    request<{ ticket: Ticket }>(`/branches/${branchId}/counters/${counterId}/walk-direct`, {
      method: 'POST',
      body: { serviceId },
      idempotent: true,
    }),

  /** The people a customer at this branch can be sent to: a name and an id each. */
  colleagues: (branchId: string) =>
    request<{ colleagues: { id: string; displayName: string }[] }>(`/branches/${branchId}/colleagues`),

  // Configuring a branch.
  //
  // Every write below is a PATCH of the fields that changed rather than a PUT of
  // the whole row: two administrators editing different fields of the same service
  // in the same minute should both get what they asked for, and a PUT would let the
  // second silently undo the first.
  //
  // Nothing here deletes. Retiring is `{ active: false }`, and it is the same call
  // as any other edit, which is the point: a retired row is still a row.

  catalogueServices: (organisation?: string) =>
    request<{ services: Service[] }>(
      organisation ? `/services?organisation=${encodeURIComponent(organisation)}` : '/services',
    ),

  createService: (body: Partial<Service> & { organisation?: string }) =>
    request<{ service: Service }>('/services', { method: 'POST', body }),

  updateService: (serviceId: string, body: Partial<Service>) =>
    request<{ service: Service }>(`/services/${serviceId}`, { method: 'PATCH', body }),

  // Visit fields. Unlike everything else on this page, delete here is real
  // deletion rather than retirement — see internal/api/fields.go — so this is the
  // one catalogue resource the client actually removes rather than only patches.
  /** The administration read: every declaration, the withdrawn ones included,
   *  because this list is the only place a withdrawn one can be found again.
   *  Needs catalogue.manage. */
  fields: (organisation?: string) =>
    request<{ fields: VisitField[] }>(
      organisation
        ? `/fields?include=retired&organisation=${encodeURIComponent(organisation)}`
        : '/fields?include=retired',
    ),
  /** The floor read: what a ticket may carry right now, for whoever is about to
   *  write a value onto one. Retired declarations are left out — the writing
   *  route refuses them, so offering one would be a box that produces a
   *  refusal. Open to anybody who may read a ticket. */
  declaredFields: async () => {
    // The floor read ignores ?organisation=; the owner reads the administration list instead.
    const org = contextOrganisation()
    if (!org) return request<{ fields: VisitField[] }>('/fields')
    const all = await request<{ fields: VisitField[] }>(`/fields?include=retired&organisation=${encodeURIComponent(org)}`)
    return { fields: all.fields.filter((f) => f.active) }
  },

  createField: (body: Partial<VisitField> & { organisation?: string }) =>
    request<{ field: VisitField }>('/fields', { method: 'POST', body }),

  updateField: (fieldId: string, body: Partial<VisitField>) =>
    request<{ field: VisitField }>(`/fields/${fieldId}`, { method: 'PATCH', body }),

  deleteField: (fieldId: string) => request<void>(`/fields/${fieldId}`, { method: 'DELETE' }),

  /** Removes every stored copy of one value under this field, on request. Not
   *  behind the licence: a lapsed licence must not block an erasure. */
  eraseFieldValue: (fieldId: string, value: string) =>
    request<{ erased: number }>(`/fields/${fieldId}/erase`, { method: 'POST', body: { value } }),

  queues: (branchId: string) => request<{ queues: Queue[] }>(`/branches/${branchId}/queues`),

  createBranch: (body: Partial<Branch> & { organisation?: string; copySettingsFrom?: string }) =>
    request<{ branch: Branch }>('/branches', { method: 'POST', body }),

  updateBranch: (branchId: string, body: Partial<Branch>) =>
    request<{ branch: Branch }>(`/branches/${branchId}`, { method: 'PATCH', body }),

  branchModules: () => request<{ modules: BranchModule[] }>('/modules'),
  /** Which per-branch modules one branch holds. Any signed-in person who may act in the branch may ask. */
  heldModules: (branchId: string) => request<{ held: string[] }>(`/branches/${branchId}/modules`),
  assignModule: (branchId: string, module: string) =>
    request<{ held: boolean }>(`/branches/${branchId}/modules/${module}`, { method: 'PUT' }),
  unassignModule: (branchId: string, module: string) =>
    request<{ held: boolean }>(`/branches/${branchId}/modules/${module}`, { method: 'DELETE' }),

  /** A branch's approval settings: who is told when a request becomes pending,
   *  and which kinds wait for a decision. Saved
   *  straight to the branch, not through its publish draft — Administration ->
   *  This branch -> Approvals (branch.manage). */
  branchApprovals: (branchId: string) =>
    request<{ settings: ApprovalSettings }>(`/branches/${branchId}/approvals`),
  saveBranchApprovals: (branchId: string, settings: ApprovalSettings) =>
    request<{ settings: ApprovalSettings }>(`/branches/${branchId}/approvals`, {
      method: 'PUT',
      body: settings,
    }),

  branchGroups: (organisation?: string) =>
    request<{ groups: BranchGroup[] }>(
      `/branch-groups${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`,
    ),
  createBranchGroup: (body: { name: string; branchIds: string[]; organisation?: string }) =>
    request<{ group: BranchGroup }>('/branch-groups', { method: 'POST', body }),
  updateBranchGroup: (groupId: string, body: { name?: string; branchIds?: string[] }) =>
    request<{ group: BranchGroup }>(`/branch-groups/${groupId}`, { method: 'PATCH', body }),
  deleteBranchGroup: (groupId: string) => request<void>(`/branch-groups/${groupId}`, { method: 'DELETE' }),

  // The installation owner's section.

  allOrganisations: () => request<{ organisations: Organisation[] }>('/organisations'),

  createOrganisation: (body: {
    name: string
    slug?: string
    hostname?: string
    timezone?: string
    staffLanguage?: StaffLanguage
  }) =>
    request<{ organisation: Organisation }>('/organisations', { method: 'POST', body }),

  updateOrganisation: (tenantId: string, body: Partial<Organisation>) =>
    request<{ organisation: Organisation }>(`/organisations/${tenantId}`, { method: 'PATCH', body }),

  /** `bands` is reports/insightBands' bandQuery, empty for the server's defaults. */
  report: (branchId: string, from: string, to: string, bands: string, signal?: AbortSignal) =>
    request<ReportSummary>(
      `/branches/${branchId}/report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${bands ? '&' + bands : ''}`,
      { signal },
    ),

  /** Whether the branch had a confirmed booking over a period: one indexed look, not the report. */
  hasBookings: (branchId: string, from: string, to: string, signal?: AbortSignal) =>
    request<{ any: boolean }>(`/branches/${branchId}/report/bookings/any?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { signal }),

  /** What became of the branch's confirmed bookings over a period, in total and by one grouping. */
  bookingReport: (branchId: string, from: string, to: string, by: BookingBy, signal?: AbortSignal) =>
    request<BookingReport>(`/branches/${branchId}/report/bookings?by=${by}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { signal }),

  /** One grouping of the branch's tickets over a period (report.Dim). `bands` is a
   *  query fragment ("waitEdges=3,5"), empty for the server's defaults. */
  reportBreakdown: (branchId: string, by: FetchedDim, from: string, to: string, bands: string, signal?: AbortSignal) =>
    request<{ by: string; rows: ReportRow[] }>(
      `/branches/${branchId}/report/breakdown?by=${by}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${bands ? '&' + bands : ''}`,
      { signal },
    ),

  /** The branch's figures grouped by two fields at once (report.Field): the cells only. */
  reportCross: (branchId: string, a: string, b: string, from: string, to: string, signal?: AbortSignal) =>
    request<{ a: string; b: string; cells: CrossCell[] }>(
      `/branches/${branchId}/report/cross?a=${a}&b=${b}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { signal },
    ),

  /** The report's Served for the branch's business day, alone: what the Floor shows. */
  servedToday: (branchId: string) =>
    request<{ businessDate: string; served: number }>(`/branches/${branchId}/report/served-today`),

  /** Every branch the person reaches, live, for the dashboard's All branches. */
  floorBranches: (organisation?: string) => request<FloorOverview>(`/floor/branches${orgQuery(organisation)}`),

  /** The dashboard's last four hours by quarter hour: one branch, or every branch the overview shows. */
  floorSeries: (branchId?: string, organisation?: string) =>
    request<FloorSeries>(branchId ? `/floor/series?branch=${encodeURIComponent(branchId)}` : `/floor/series${orgQuery(organisation)}`),

  /** Every branch the signed-in person may see, side by side.
   *
   *  The one endpoint the analytics module gates, and the refusal it answers with
   *  where the licence does not cover it is a 402 carrying MODULE_NOT_LICENSED.
   *  Callers are expected to have asked offersNetworkView first: a screen that
   *  offers this where it was not bought has built a button whose only answer is a
   *  refusal.
   *
   *  No branch is named. The server answers for the branches the caller can reach,
   *  which is a permission question and not this screen's to ask. */
  networkReport: (from: string, to: string, group?: string, signal?: AbortSignal) =>
    request<NetworkSummary>(
      `/reports/network?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${
        group ? `&group=${encodeURIComponent(group)}` : ''
      }`,
      { signal },
    ),

  /** The same report as a file. A link rather than a fetch, so the browser saves it
   *  with the name the server chose instead of one invented here. */
  reportCSV: (branchId: string, from: string, to: string, bands: string) =>
    `${BASE}/branches/${branchId}/report.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${bands ? '&' + bands : ''}`,

  /** The period on one page, in the reader's language; a link for the same reason. */
  reportPDF: (branchId: string, from: string, to: string, bands: string) =>
    `${BASE}/branches/${branchId}/report.pdf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${bands ? '&' + bands : ''}`,

  /** One page of the per-ticket report. `query` is reports/ticketReport's
   *  ticketQuery, which names the branches and the filters. */
  ticketReport: (query: string, signal?: AbortSignal) => request<TicketReportPage>(`/reports/tickets?${query}`, { signal }),

  /** Distinct tickets and steps the filters keep, and the services and people in
   *  the period to filter by. */
  ticketReportSummary: (query: string, signal?: AbortSignal) =>
    request<TicketReportSummary>(`/reports/tickets/summary?${query}`, { signal }),

  /** The server's file of exactly what is filtered. A link rather than a fetch,
   *  so the browser saves a large period as it arrives and builds nothing. */
  ticketReportFile: (query: string, kind: 'xlsx' | 'csv') => `${BASE}/reports/tickets.${kind}?${query}`,

  /** The whole catalogue of outcomes, retired ones included: the administration
   *  list, which is how a retired one is found again. */
  /** A branch's machines, retired ones included: the administration list. */
  units: (branchId: string) => request<{ units: Unit[] }>(`/branches/${branchId}/units`),

  /** Everything an unattended screen is told about itself, in one call.
   *
   *  No session. A hall board and a kiosk are reached by address alone: a
   *  credential in a browser on a machine in a public lobby is a credential
   *  anybody can walk up to, and a television is not a thing that logs in. What
   *  makes it safe is that this endpoint and the socket beside it answer with the
   *  redacted view — the numbers, the desks and the depth of each queue, never who
   *  is behind the counter or how the branch is performing. */
  screen: (branchCode: string, unitCode: string) =>
    request<ScreenView>(
      `/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}`,
    ),

  /** A kiosk handing out a number, by address. The machine decides the source and
   *  the priority — a lobby machine cannot promote its own customer. */
  screenTicket: (
    branchCode: string,
    unitCode: string,
    body: {
      serviceId: string
      language?: string
      /** Only from a screen app, and only the keys its manifest names; the server
       *  refuses any other key, and any field at all on a machine running no app,
       *  as FIELDS_REFUSED. */
      fields?: Record<string, string>
      /** The kiosk pages the customer came through, by id, the menu left out. The
       *  server names them from the published design; an unknown id is skipped
       *  and never refuses the ticket. */
      menuPath?: string[]
    },
  ) =>
    request<{
      ticket: Ticket
      /** Where this ticket stands, first being 1. Zero when the server could not
       *  count, which is unknown and never "next": a waiting ticket is at least
       *  first. */
      position?: number
      estimatedWaitSec?: number
      estimate?: Estimate | null
      /** What this ticket carries that its declarations permit on a kiosk's own
       *  surface — filtered on the server (screenValues in
       *  internal/api/screen.go), never here. Absent or empty for every ticket a
       *  kiosk issues today, since nothing collects a value at one yet. */
      values?: Record<string, string> | null
      /** The slip's follow link; absent when the installation prints none. */
      followUrl?: string
      /** The slip's validity, RFC 3339 in the branch's offset; absent prints nothing. */
      validUntil?: string
    }>(`/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/ticket`, {
      method: 'POST',
      body,
      idempotent: true,
    }),

  /** A feedback tablet asking which visit is at its desk. No session: the address
   *  is the machine. See internal/api/feedback.go. */
  screenFeedback: (branchCode: string, unitCode: string) =>
    request<{ visit: FeedbackVisit | null }>(
      `/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/feedback`,
    ),

  /** A counter display asking about the ticket at its desk: asked when the desk's
   *  ticket changes, never on a timer. Refused NOT_A_DESK_DISPLAY, DESK_TOO_OFTEN. */
  screenDesk: (branchCode: string, unitCode: string) =>
    request<{ visit: CounterVisit | null; clerk?: string }>(
      `/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/desk`,
    ),

  /** A press on the tablet: the visit it was showing and the button. Believed only
   *  from the machine holding the tablet's live channel, which proves it with the
   *  token it was handed there (X-Press-Token). 201 recorded, 200 already given. */
  screenPress: (
    branchCode: string,
    unitCode: string,
    pressToken: string,
    body: { ticketId: string; code: string },
  ) =>
    request<{ recorded: boolean }>(
      `/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/feedback`,
      { method: 'POST', body, headers: { 'X-Press-Token': pressToken } },
    ),

  /** A kiosk arriving a booked customer by the code on their confirmation. The
   *  booking comes back as the lobby may know it — its time and service — and the
   *  ticket without its values. Unlike screenTicket there are no `values` at all:
   *  a booking's values name a person, and the code was typed by whoever stood at
   *  the machine, so nothing of them goes on the screen or the slip. */
  screenArrive: (branchCode: string, unitCode: string, body: { code: string; language?: string }) =>
    request<{
      ticket: Ticket
      position?: number
      estimate?: Estimate | null
      followUrl?: string
      /** The slip's validity, RFC 3339 in the branch's offset; absent prints nothing. */
      validUntil?: string
      appointment: { id: string; startsAt: string; serviceId: string; businessDate: string }
    }>(`/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/arrive`, {
      method: 'POST',
      body,
    }),

  /** An uncaught staff-app error, for the server's log (lib/clientErrors.ts). */
  clientError: (body: ClientErrorReport) => request<void>('/client-errors', { method: 'POST', body }),

  /** A line for the server's log, from a machine: qms.log in the host API. Tagged
   *  on the server with the machine's address, cut to 500 characters and limited to
   *  sixty a minute; over the limit the server drops it and still answers 204, so
   *  a noisy screen is never handed an error it would log in turn. No app label:
   *  the server says which app wrote it, from what is placed on the machine. */
  screenLog: (
    branchCode: string,
    unitCode: string,
    body: {
      level: string
      message: string
      skipped?: { assetId: string; kind: 'image' | 'video' }
      cache?: { files: number; bytes: number; limit: number }
    },
  ) =>
    request<void>(`/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/log`, {
      method: 'POST',
      body,
    }),

  /** A call through a connector, from a machine: qms.call in the host API. The
   *  server makes the request and answers with what the receiver said, whatever
   *  its status; a refusal (an undeclared or unknown connector, one switched off,
   *  the rate limit, a timeout, a receiver that could not be reached) arrives as
   *  an ApiError, which the host turns into the app's `{ ok: false, reason }`
   *  (callRefusalOf in host/machineHost.ts). */
  screenCall: (
    branchCode: string,
    unitCode: string,
    connector: string,
    body: { method?: string; path?: string; query?: Record<string, string>; body?: unknown },
    signal?: AbortSignal,
  ) =>
    request<{ status: number; contentType: string; body: unknown }>(
      `/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/call/${encodeURIComponent(connector)}`,
      { method: 'POST', body, signal },
    ),

  /** Which app is placed on a machine, and its attribute values. A different
   *  route from the unit's settings: this one reaches the running machine at once
   *  (docs/EXTENSIBILITY.md section 3), rather than waiting for nothing since
   *  machines are never staged. `appId` empty clears it, running the design again. */
  placeScreenApp: (unitId: string, body: { appId: string; attributes: Record<string, unknown> }) =>
    request<{ unit: Unit }>(`/units/${unitId}/app`, { method: 'PUT', body }),

  /** Whether a machine has a key, and whether this server refuses one without (internal/api/screenkey.go). */
  screenKeyStatus: (unitId: string) =>
    request<{ hasKey: boolean; issuedAt?: string; required: boolean }>(`/units/${unitId}/screen-key`),
  /** A new key, answered once inside the address to open on the machine; the old key stops. */
  issueScreenKey: (unitId: string) =>
    request<{ key: string; path: string; url: string; issuedAt: string }>(`/units/${unitId}/screen-key`, {
      method: 'POST',
      body: {},
    }),

  /** An organisation's uploaded screen apps, with every version and how many
   *  machines each currently runs. `organisation` is for the installation's
   *  owner, as on Webhooks and Directory. */
  screenApps: (organisation?: string) =>
    request<{ apps: ScreenApp[] }>(
      organisation ? `/screen-apps?organisation=${encodeURIComponent(organisation)}` : '/screen-apps',
    ),

  /** Uploads a zip as a new app, or a new version of one already here (the
   *  manifest's own id decides which). The answer's detail already names every
   *  fault the zip or the manifest had. */
  uploadScreenApp: async (file: File, organisation?: string): Promise<ScreenApp> => {
    const body = new FormData()
    if (organisation) body.append('organisation', organisation)
    body.append('file', file)
    const response = await fetch(`${BASE}/screen-apps`, { method: 'POST', body, credentials: 'same-origin' })
    const text = await response.text()
    const payload = text ? safeParse(text) : undefined
    if (!response.ok) {
      const problem = payload as
        | {
            code?: string
            detail?: string
            title?: string
            correlationId?: string
            args?: Record<string, string>
            reason?: string
          }
        | undefined
      throw new ApiError(
        response.status,
        problem?.code ?? 'UNKNOWN',
        problem?.detail ?? problem?.title ?? `The server answered ${response.status}.`,
        problem?.correlationId,
        problem?.args,
        problem?.reason,
      )
    }
    return (payload as { app: ScreenApp }).app
  },

  /** Rolls an app forward or back: makes the named version current. Every unit
   *  placed on the app reconciles its attributes against it at once. */
  setCurrentScreenAppVersion: (appId: string, versionId: string) =>
    request<{ app: ScreenApp }>(`/screen-apps/${appId}/current`, { method: 'PUT', body: { versionId } }),

  /** Refused (409 APP_PLACED) while any machine still runs the app. */
  retireScreenApp: (appId: string) => request<{ app: ScreenApp }>(`/screen-apps/${appId}`, { method: 'DELETE' }),

  /** Mints a capability URL for one version's entry, good for 15 minutes and
   *  served with no session at all (why is in internal/api/screenapps.go). A new
   *  one is minted on every Preview click rather than kept, since it expires. */
  previewScreenApp: (appId: string, versionId: string) =>
    request<{ url: string }>(`/screen-apps/${appId}/versions/${versionId}/preview`, { method: 'POST', body: {} }),

  outcomes: (organisation?: string) =>
    request<{ outcomes: Outcome[] }>(
      organisation ? `/outcomes?organisation=${encodeURIComponent(organisation)}` : '/outcomes',
    ),

  createOutcome: (body: Partial<Outcome> & { organisation?: string }) =>
    request<{ outcome: Outcome }>('/outcomes', { method: 'POST', body }),

  updateOutcome: (id: string, body: Partial<Outcome>) =>
    request<{ outcome: Outcome }>(`/outcomes/${id}`, { method: 'PATCH', body }),

  /** What may be recorded against one service, right now: retired outcomes left
   *  out, the ones offered for every service included. What a desk asks. */
  serviceOutcomes: (serviceId: string) =>
    request<{ outcomes: Outcome[] }>(`/services/${serviceId}/outcomes`),

  /** The whole catalogue of marks, retired ones included: the administration
   *  list, which is how a retired one is found again. */
  marks: (organisation?: string) =>
    request<{ marks: Mark[] }>(
      organisation ? `/marks?organisation=${encodeURIComponent(organisation)}` : '/marks',
    ),

  createMark: (body: Partial<Mark> & { organisation?: string }) =>
    request<{ mark: Mark }>('/marks', { method: 'POST', body }),

  updateMark: (id: string, body: Partial<Mark>) =>
    request<{ mark: Mark }>(`/marks/${id}`, { method: 'PATCH', body }),

  /** What else may be noted against one service, right now. Empty is the ordinary
   *  answer rather than a failure — marks are never required, so a desk that gets
   *  an empty list is simply shown no control. */
  serviceMarks: (serviceId: string) => request<{ marks: Mark[] }>(`/services/${serviceId}/marks`),

  /** The whole tree of delivered services, retired ones and their outcomes
   *  included: the administration list. */
  deliveredServices: (organisation?: string) =>
    request<{ deliveredServices: DeliveredService[] }>(
      organisation
        ? `/delivered-services?organisation=${encodeURIComponent(organisation)}`
        : '/delivered-services',
    ),

  createDeliveredService: (body: Partial<DeliveredService>) =>
    request<{ deliveredService: DeliveredService }>('/delivered-services', { method: 'POST', body }),

  updateDeliveredService: (id: string, body: Partial<DeliveredService>) =>
    request<{ deliveredService: DeliveredService }>(`/delivered-services/${id}`, {
      method: 'PATCH',
      body,
    }),

  createDeliveredOutcome: (deliveredServiceId: string, body: Partial<DeliveredOutcome>) =>
    request<{ outcome: DeliveredOutcome }>(`/delivered-services/${deliveredServiceId}/outcomes`, {
      method: 'POST',
      body,
    }),

  updateDeliveredOutcome: (id: string, body: Partial<DeliveredOutcome>) =>
    request<{ outcome: DeliveredOutcome }>(`/delivered-outcomes/${id}`, { method: 'PATCH', body }),

  /** What a desk may record under one service, right now: the active delivered
   *  services with their active outcomes. Empty means nothing is asked. */
  serviceDelivered: (serviceId: string) =>
    request<{ deliveredServices: DeliveredService[] }>(`/services/${serviceId}/delivered-services`),

  /** The audit trail, newest first.
   *
   *  Paged by time rather than by offset: the trail is appended to while it is
   *  being read, and an offset into a growing table shows some rows twice and skips
   *  others. `nextBefore` and `nextBeforeId` from one answer are `before` and
   *  `beforeId` on the next. */
  auditEvents: (query: {
    action?: string
    outcome?: string
    branch?: string
    actor?: string
    since?: string
    until?: string
    before?: string
    beforeId?: string
    limit?: number
  }) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value))
    }
    const qs = params.toString()
    return request<{ events: AuditEvent[]; nextBefore?: string; nextBeforeId?: string }>(`/audit${qs ? `?${qs}` : ''}`)
  },

  /** Walks a branch's whole event chain and reports whether it is intact.
   *
   *  Not something a screen polls: it reads every event in the branch. It is what a
   *  person runs when somebody asks whether the record can be trusted. */
  verifyChain: (branchId: string) =>
    request<{
      intact: boolean
      checked: number
      problem?: string
      /** Present when the walk did not start at the beginning, because a prune has
       *  removed what came before. "Intact" from a checkpoint is a weaker statement
       *  than "intact" from the first event ever recorded, and the screen has to be
       *  able to tell them apart. */
      from?: {
        throughSeq: number
        throughAt: string
        archive: string
        archiveSha: string
        rowsRemoved: number
      }
    }>(`/branches/${branchId}/audit/verify`),

  settings: () => request<{ settings: SettingView[] }>('/settings'),

  /**
   * Changes settings. A name mapped to null is being put back to the configuration
   * file, which is a different act from setting it to the file's current value —
   * the first follows the file afterwards and the second does not.
   */
  saveSettings: (values: Record<string, string | null>) =>
    request<{ settings: SettingView[] }>('/settings', { method: 'PATCH', body: { values } }),

  licence: () => request<LicenceStatus>('/licence'),

  /**
   * Uploads a licence file as its own bytes.
   *
   * Not wrapped in JSON, and not read as text and re-sent: the signature covers
   * exactly the bytes the customer was given, so anything that re-encodes them on
   * the way produces a file the server can never verify again.
   */
  applyLicence: (file: File) => postFile<LicenceStatus>('/licence', file),

  /** The request file sent to Qmeter for a licence: installation code and machines. */
  licenceRequest: () => request<unknown>('/licence/request'),

  /** Uploads a signed revocation list (.qrl) as its own bytes, as applyLicence does. */
  applyRevocations: (file: File) => postFile<LicenceStatus>('/licence/revocations', file),

  // ---- Profiles (F0) ----
  // docs/PROFILES-API.md.

  operationProfiles: (organisation?: string) =>
    request<{ profiles: OperationProfileRow[] }>(
      `/operation-profiles${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`,
    ),
  createOperationProfile: (body: { name: string; description?: string; organisation?: string }) =>
    request<{ profile: OperationProfileRow; equipmentProfile: EquipmentProfileRow }>('/operation-profiles', {
      method: 'POST',
      body,
    }),
  updateOperationProfile: (id: string, body: { name?: string; description?: string }) =>
    request<{ profile: OperationProfileRow }>(`/operation-profiles/${id}`, { method: 'PATCH', body }),
  deleteOperationProfile: (id: string) => request<void>(`/operation-profiles/${id}`, { method: 'DELETE' }),
  operationDocument: (id: string) => request<OperationDocumentAnswer>(`/operation-profiles/${id}/document`),
  saveOperationDocument: (id: string, body: { baseRevision: number; document: OperationDoc }) =>
    request<{ revision: number; warnings: ProfileProblem[]; notLive?: NotLive; starterWorkProfile?: string }>(`/operation-profiles/${id}/document`, {
      method: 'PUT',
      body,
    }),
  duplicateOperationProfile: (id: string, body: { name: string }) =>
    request<{ profile: OperationProfileRow; equipmentProfiles: EquipmentProfileRow[] }>(
      `/operation-profiles/${id}/duplicate`,
      { method: 'POST', body },
    ),
  exportOperationProfileUrl: (id: string) => `${BASE}/operation-profiles/${id}/export`,
  previewImportOperationProfile: (body: ImportRequest) =>
    request<ProfileImportPreview>('/operation-profiles/import?preview=1', { method: 'POST', body }),
  importOperationProfile: (body: ImportRequest) =>
    request<{ profile: OperationProfileRow }>('/operation-profiles/import', { method: 'POST', body }),

  equipmentProfiles: (operationProfileId: string) =>
    request<{ equipmentProfiles: EquipmentProfileRow[] }>(`/operation-profiles/${operationProfileId}/equipment-profiles`),
  createEquipmentProfile: (operationProfileId: string, body: { name: string; description?: string }) =>
    request<{ equipmentProfile: EquipmentProfileRow }>(`/operation-profiles/${operationProfileId}/equipment-profiles`, {
      method: 'POST',
      body,
    }),
  updateEquipmentProfile: (id: string, body: { name?: string; description?: string }) =>
    request<{ equipmentProfile: EquipmentProfileRow }>(`/equipment-profiles/${id}`, { method: 'PATCH', body }),
  deleteEquipmentProfile: (id: string) => request<void>(`/equipment-profiles/${id}`, { method: 'DELETE' }),
  equipmentDocument: (id: string) => request<EquipmentDocumentAnswer>(`/equipment-profiles/${id}/document`),
  saveEquipmentDocument: (id: string, body: { baseRevision: number; document: EquipmentDoc }) =>
    request<{ revision: number; document: EquipmentDoc; warnings: ProfileProblem[]; notLive?: NotLive }>(`/equipment-profiles/${id}/document`, {
      method: 'PUT',
      body,
    }),
  /** A copy in the same operation profile; the server names it "Copy of …" unless told. */
  duplicateEquipmentProfile: (id: string, name?: string) =>
    request<{ equipmentProfile: EquipmentProfileRow }>(`/equipment-profiles/${id}/duplicate`, {
      method: 'POST',
      body: name ? { name } : {},
    }),

  branchSetup: (branchId: string) => request<BranchSetupAnswer>(`/branches/${branchId}/config`),
  saveBranchSetup: (branchId: string, body: { config: BranchDoc; baseRevision: number }) =>
    request<SaveBranchSetupAnswer>(`/branches/${branchId}/config/draft`, { method: 'PUT', body }),
  branchSetupVersions: (branchId: string) =>
    request<{ versions: SetupVersion[] }>(`/branches/${branchId}/config/versions`),
  restoreBranchSetup: (branchId: string, version: number) =>
    request<{ version: number; config: BranchSetup }>(`/branches/${branchId}/config/restore/${version}`, {
      method: 'POST',
      body: {},
    }),
  setupStatus: (organisation?: string) =>
    request<{ branches: SetupStatusLine[] }>(
      `/config/status${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`,
    ),
  /** Switching a desk's profile; `pending` when it waits for the visit to end. */
  changeSessionProfile: (counterId: string, workProfileId: string) =>
    request<{ session: CounterSessionView; pending?: boolean }>(`/counters/${counterId}/session/profile`, {
      method: 'POST',
      body: { workProfileId },
    }),
  /** R9: a design is chosen by the default flag and, for a slip, services; branch and machine choices are gone. */
  setLayoutUse: (layoutId: string, body: { default: boolean; serviceIds?: string[] }) =>
    request<{ layout: StoredLayout }>(`/layouts/${layoutId}/assignment`, { method: 'PUT', body }).then((r) => ({ layout: unpackStored(r.layout) })),
  /** Per branch, how many machines of the design's kind run there and how many chose it. */
  layoutBranches: (layoutId: string) =>
    request<{ branches: DesignBranch[]; canApply: boolean }>(`/layouts/${layoutId}/branches`),
  /** Applies the design to every machine of its kind at the apply branches, drops it at remove, and publishes each. */
  applyLayoutBranches: (layoutId: string, body: { apply: string[]; remove: string[] }) =>
    request<{ results: DesignResult[]; batch: string }>(`/layouts/${layoutId}/branches`, { method: 'PUT', body }),
  // ---- end Profiles (F0) ----

  // ---- Wording (docs/i18n.md); the owner names the organisation ----
  wording: (organisation?: string) =>
    request<WordingView>(`/organisation/wording${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`),
  saveWording: (body: { key: string; lang: string; text: string }, organisation?: string) =>
    request<WordingOverride>(`/organisation/wording${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`, {
      method: 'PUT',
      body,
    }),
  resetWording: (key: string, lang: string, organisation?: string) => {
    const q = new URLSearchParams({ key, lang, ...(organisation ? { organisation } : {}) })
    return request<void>(`/organisation/wording?${q}`, { method: 'DELETE' })
  },
  // ---- Organisation languages (internal/api/languages.go) ----
  orgLanguages: (organisation?: string) =>
    request<OrgLanguagesView>(`/organisation/languages${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`),
  saveOrgLanguages: (body: LanguageSettings, organisation?: string) =>
    request<OrgLanguagesView>(`/organisation/languages${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`, {
      method: 'PUT',
      body,
    }),

  // ---- A branch's settings (org.BranchSettings) and the organisation's break reasons ----
  branchSettings: (branchId: string) => request<{ settings: BranchSettingsView }>(`/branches/${branchId}/settings`),
  saveBranchSettings: (branchId: string, change: BranchSettingsPatch) =>
    request<{ settings: BranchSettingsView }>(`/branches/${branchId}/settings`, { method: 'PATCH', body: change }),
  /** Copies the named settings of one branch to the others, in one go. */
  applyBranchSettings: (branchId: string, body: { settings: SettingKey[]; branchIds: string[] }) =>
    request<{ branches: number; changed: number }>(`/branches/${branchId}/settings/apply`, { method: 'POST', body }),
  createBranchReason: (branchId: string, body: { name: string; kind?: BreakKind; sortOrder?: number }) =>
    request<{ reason: BreakReason }>(`/branches/${branchId}/break-reasons`, { method: 'POST', body }),
  updateBranchReason: (branchId: string, reasonId: string, body: { name?: string; kind?: BreakKind; sortOrder?: number }) =>
    request<{ reason: BreakReason }>(`/branches/${branchId}/break-reasons/${reasonId}`, { method: 'PATCH', body }),
  deleteBranchReason: (branchId: string, reasonId: string) =>
    request<void>(`/branches/${branchId}/break-reasons/${reasonId}`, { method: 'DELETE' }),
  breakReasons: (organisation?: string) =>
    request<{ reasons: BreakReason[] }>(`/break-reasons${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`),
  createBreakReason: (body: { name: string; kind?: BreakKind; sortOrder?: number }, organisation?: string) =>
    request<BreakReason>('/break-reasons', { method: 'POST', body: { ...body, ...(organisation ? { organisation } : {}) } }),
  updateBreakReason: (
    reasonId: string,
    body: { name?: string; kind?: BreakKind; sortOrder?: number },
    organisation?: string,
  ) =>
    request<BreakReason>(`/break-reasons/${reasonId}`, {
      method: 'PATCH',
      body: { ...body, ...(organisation ? { organisation } : {}) },
    }),
  deleteBreakReason: (reasonId: string, organisation?: string) =>
    request<void>(`/break-reasons/${reasonId}${organisation ? `?organisation=${encodeURIComponent(organisation)}` : ''}`, {
      method: 'DELETE',
    }),
}
